import React, { useState, useCallback, useEffect } from "react";
import { AlertTriangle, FileText, X } from "lucide-react";
import { BFS_MAPPING } from "./HouseholdSetupWizard/WizardConstants";
import {
  DEPOSIT_METHOD_OPTIONS,
  BANK_ACCOUNT_TYPE_OPTIONS,
  WITHDRAWAL_METHOD_OPTIONS,
  FREQUENCY_OPTIONS,
  DESTINATION_TYPE_OPTIONS,
  TYPE_OF_TRANSFER_OPTIONS,
  PROCEEDS_OPTIONS,
  SECURITY_PURCHASES_OPTIONS,
  DIVIDEND_OPTIONS,
  DEFAULT_DIVIDEND_OPTION,
  LIQUIDITY_NEEDS_OPTIONS,
  LIQUIDITY_TIME_HORIZON_OPTIONS,
  getLiquidityNeedsLabel,
  getLiquidityTimeHorizonLabel,
  parseLiquidityNeedsDisplay,
  parseLiquidityTimeHorizonDisplay,
  REGISTRATION_OPTIONS,
  getAccountTypesForRegistration,
  getTaxClassificationOptions,
} from "../constants/accountConstants";
import {
  PRIMARY_OBJECTIVE_OPTIONS,
  RISK_TOLERANCE_OPTIONS,
  DECISION_MAKING_OPTIONS,
  SOURCE_OF_FUNDS_OPTIONS,
  PRIMARY_USE_OPTIONS,
  ACCOUNT_TIME_HORIZON_OPTIONS,
  getAccountRiskTimeHorizonLabel,
  parseAccountRiskTimeHorizonDisplay,
} from "../constants/householdConstants";
import axiosInstance from "../api/axiosInstance";
import { updateHouseholdAPI } from "../api/updateHouseholdAPI";
import {
  formatAccountName,
  formatDateForInput,
  getAccountOwnerFirstNamesLabel,
  isRetirementAccount,
} from "../utils/formatters";
import {
  formatNumericInput,
  formatRoutingNumberInput,
  validateRoutingNumber,
  formatPhoneInput,
  formatZipInput,
  formatSSNInput,
  formatAccountNumberInput,
  validatePhoneDigits,
  validateZipDigits,
  getTaxIdCountError,
  countSSNDigits,
  validateLplAccountNumber,
} from "../utils/validationHelpers";
import StateCitySelect from "./StateCitySelect";
import MemberSelect from "./shared/MemberSelect";
import { useToast } from "../context/ToastContext";
import { humanizeError } from "../utils/userFriendlyError";
import { US_STATES } from "../data/usStatesAndCities";
import DateInput from "./DateInput";
import {
  getDatePickerBoundsForConstraint,
  validatePastOnlyYmd,
  validateScheduleFutureYmd,
} from "../utils/dateFieldConstraints";
import {
  isRevocableTrust,
  getTrusteeMemberId,
  trusteeEntryToObject,
  hasDuplicateTrusteeMemberIds,
  isPlaceholderTrusteeRelationship,
  finalizeTrusteesForPayload,
  getTrustGrantorIds,
  buildTrustInfoUpdatePayload,
} from "../utils/trustInfoHelpers";
import ShareSlider, { SharePresetButtons } from "./ShareSlider";
import {
  BENEFICIARY_TOTAL_EPS,
  isBeneficiaryShareTotalOver100,
} from "../api/updateHouseholdAPI";
import { RELATIONSHIP_OPTIONS } from "../constants/membersConstants";
import { deliveringFirmOptions } from "../constants/deliveringFirms";
import {
  NON_EDITABLE_PENDING_FIELD_MESSAGES,
  isNonEditablePendingField,
} from "../constants/nonEditablePendingFields";
import {
  BANK_TRACKED_KEYS,
  canonicalBankAccountType,
  formatBankLabel,
  getAccountBanks,
  getUnlinkedHouseholdBanks,
  resolveAccountBankIssues,
  filterBankIssuesByReason,
} from "../utils/bankAccounts";
import {
  computeBrokerageFlags,
  isFieldOptionalForIws,
  showAccountDecisionMaking,
  showSourceOfFunds,
  showUseOfFunds,
  showLiquidityRequirements,
  showAccountRiskDetails,
} from "../utils/brokerageFields";
import { useRelationshipPrefillMany } from "../hooks/useRelationshipPrefill";

const isBlank = (v) => v == null || (typeof v === "string" && v.trim() === "");

/** Client PUT may return a full household or wrap it; otherwise caller can merge optimistically. */
const householdFromClientPutResponse = (data) => {
  if (!data || typeof data !== "object") return null;
  if (Array.isArray(data.accounts)) return data;
  if (data.household && typeof data.household === "object") return data.household;
  return null;
};

const mergeAccountIntoHouseholdForState = (household, accountId, updatedAccount) => {
  if (!household || !accountId || !updatedAccount || typeof updatedAccount !== "object") {
    return null;
  }
  return {
    ...household,
    accounts: (household.accounts || []).map((a) =>
      a.id === accountId ? { ...a, ...updatedAccount } : a,
    ),
    missing_fields: household.missing_fields,
  };
};

/** Drop a saved account field from local missing_fields (optimistic merge keeps stale pending list otherwise). */
const stripSavedAccountMissingField = (household, accountId, savedField) => {
  if (
    !household ||
    !accountId ||
    !savedField ||
    !household.missing_fields ||
    typeof household.missing_fields !== "object"
  ) {
    return household;
  }
  const accRaw = household.missing_fields.accounts;
  if (!accRaw || typeof accRaw !== "object") return household;
  const cur = accRaw[accountId];
  if (!Array.isArray(cur) || !cur.includes(savedField)) return household;
  const nextList = cur.filter((f) => f !== savedField);
  const nextAcc = { ...accRaw };
  if (nextList.length === 0) {
    delete nextAcc[accountId];
  } else {
    nextAcc[accountId] = nextList;
  }
  const nextMf = { ...household.missing_fields };
  if (Object.keys(nextAcc).length === 0) {
    delete nextMf.accounts;
  } else {
    nextMf.accounts = nextAcc;
  }
  const noHousehold = !nextMf.household?.length;
  const noMembers =
    !nextMf.household_members ||
    !Object.keys(nextMf.household_members).some(
      (k) => nextMf.household_members[k]?.length > 0,
    );
  const noAccounts =
    !nextMf.accounts ||
    !Object.keys(nextMf.accounts).some((k) => nextMf.accounts[k]?.length > 0);
  if (noHousehold && noMembers && noAccounts) {
    return { ...household, missing_fields: null };
  }
  return { ...household, missing_fields: nextMf };
};

const getMemberSsnDigitsById = (memberId, members) => {
  if (!memberId) return "";
  const member = (members || []).find((m) => m.id === memberId);
  const raw = member?.personal_info?.ssn_or_tin;
  return String(raw || "").replace(/\D/g, "");
};

const getMemberSsnRawById = (memberId, members) => {
  if (!memberId) return "";
  const member = (members || []).find((m) => m.id === memberId);
  return String(member?.personal_info?.ssn_or_tin ?? "");
};

const isTrustSsnMissing = (t, members, accountType) => {
  const isRevocable = isRevocableTrust(accountType);
  if (isRevocable) {
    const primaryGrantorId = getTrustGrantorIds(t)[0] || "";
    return countSSNDigits(getMemberSsnRawById(primaryGrantorId, members)) !== 9;
  }
  const raw = t?.ssn_or_tin;
  if (raw == null || raw === "") return true;
  return countSSNDigits(String(raw)) !== 9;
};

const getTrusteeRelationshipToGrantor = (trustee) => {
  if (trustee == null) return null;
  if (typeof trustee === "object" && !Array.isArray(trustee)) {
    return trustee.relationship_to_grantor ?? null;
  }
  return null;
};

const hasAnyTrusteeMemberId = (arr) =>
  Array.isArray(arr) && arr.some((x) => getTrusteeMemberId(x));

const isTrustTrusteeMembersMissing = (t) => {
  const arr = t?.trustees;
  if (!Array.isArray(arr) || arr.length === 0) return true;
  if (!hasAnyTrusteeMemberId(arr)) return true;
  return arr.some((x) => !getTrusteeMemberId(x));
};

const isTrustTrusteeRelationshipMissing = (t) => {
  const arr = t?.trustees;
  if (!hasAnyTrusteeMemberId(arr)) return false;
  const grantorId = getTrustGrantorIds(t)[0] || "";
  return arr.some((x) => {
    const id = getTrusteeMemberId(x);
    if (!id) return true;
    if (grantorId && String(id) === grantorId) return false;
    return isPlaceholderTrusteeRelationship(getTrusteeRelationshipToGrantor(x));
  });
};

const trusteeMemberIdsForSelect = (trustees) =>
  (Array.isArray(trustees) ? trustees : [])
    .map((x) => getTrusteeMemberId(x))
    .filter(Boolean);

function normalizeTrusteesForForm(trustees) {
  const arr = Array.isArray(trustees) ? trustees : [];
  return JSON.parse(JSON.stringify(arr)).map((entry) => {
    if (typeof entry === "string") {
      const id = entry.trim();
      return { id, relationship_to_grantor: null };
    }
    if (typeof entry === "object" && entry && !Array.isArray(entry)) {
      const id = getTrusteeMemberId(entry) || "";
      const base = trusteeEntryToObject(entry);
      return {
        ...base,
        id,
        relationship_to_grantor: entry.relationship_to_grantor ?? null,
      };
    }
    return { id: "", relationship_to_grantor: null };
  });
}

function normalizeTrusteesPayload(trustees, grantorMemberId = null) {
  if (!Array.isArray(trustees)) return [];
  const mapped = trustees
    .map((t) => {
      if (typeof t === "string") {
        const id = t.trim();
        return id ? { id, relationship_to_grantor: null } : null;
      }
      const id = getTrusteeMemberId(t);
      if (!id) return null;
      const o = trusteeEntryToObject(t);
      const rel = o.relationship_to_grantor;
      const relStr =
        rel == null || rel === "" ? null : String(rel).trim();
      return { ...o, id, relationship_to_grantor: relStr };
    })
    .filter(Boolean);
  return finalizeTrusteesForPayload(mapped, grantorMemberId);
}

const parseAccountTimeHorizon = (displayValue) =>
  parseAccountRiskTimeHorizonDisplay(displayValue);

const formatAccountTimeHorizon = (timeHorizon) =>
  getAccountRiskTimeHorizonLabel(timeHorizon, "");

const formatLiquidityNeeds = (amountRange) =>
  getLiquidityNeedsLabel(amountRange, "");

const formatLiquidityTimeHorizon = (timeHorizonRange) => {
  return getLiquidityTimeHorizonLabel(timeHorizonRange, "");
};

function MissingAccountFieldModal({
  isOpen,
  onClose,
  selectedField,
  account,
  household,
  custodian,
  brokerageFlags: brokerageFlagsProp,
  bankAccountId,
  onHouseholdUpdate,
  clientToken,
  household_id,
  onSaveAndNext,
}) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [bankDocUploading, setBankDocUploading] = useState(false);
  const [bankDocError, setBankDocError] = useState("");
  const [bankDocUploaded, setBankDocUploaded] = useState(false);
  const [bankDocFileName, setBankDocFileName] = useState("");
  const [bankDocSelectedFile, setBankDocSelectedFile] = useState(null);

  const handleBankDocUpload = async (file) => {
    if (!file || !household?.id) return false;
    setBankDocError("");
    setBankDocUploaded(false);
    setBankDocFileName(file.name);
    setBankDocUploading(true);
    try {
      const batchId =
        (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const presignedResponse = await axiosInstance.post(
        `/api/files/households/${household.id}/presigned-urls`,
        {
          files: [{ file_name: file.name, content_type: file.type || "application/octet-stream" }],
          batch_id: batchId,
          project_id: household.project_id,
        },
      );
      const presignedUrls = presignedResponse.data?.presigned_urls;
      if (!presignedUrls || presignedUrls.length === 0) {
        throw new Error("No presigned URL received from server");
      }
      const presignedUrlData = presignedUrls[0];
      const signedContentType =
        new URL(presignedUrlData.presigned_url).searchParams.get("content-type") ||
        file.type ||
        "application/octet-stream";
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.addEventListener("load", () => {
          if (xhr.status === 200 || xhr.status === 204) resolve();
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        });
        xhr.addEventListener("error", () => reject(new Error("Upload failed")));
        xhr.open("PUT", presignedUrlData.presigned_url);
        xhr.setRequestHeader("Content-Type", signedContentType);
        xhr.send(file);
      });
      await axiosInstance.post(
        `/api/files/households/${household.id}/confirm-upload`,
        { batch_id: batchId, project_id: household.project_id },
      );
      setBankDocUploaded(true);
      setBankDocSelectedFile(null);
      if (onHouseholdUpdate) onHouseholdUpdate();
      return true;
    } catch (err) {
      console.error("Bank document upload error:", err);
      setBankDocError(
        err?.response?.data?.detail || err.message || "Failed to upload document. Please try again.",
      );
      return false;
    } finally {
      setBankDocUploading(false);
    }
  };
  const brokerageFlags =brokerageFlagsProp ?? computeBrokerageFlags({
    custodian: household?.custodian ?? custodian,
    custodianTypes: household?.custodian_types,
    destinationFirm: household?.destination_firm,
  });

  useEffect(() => {
    if (isOpen && account && selectedField) {
      setSaveError("");
      if (selectedField === "withdrawals") {
        // Map backend field `destination_type` → UI field `destination` for the dropdown.
        const mappedWithdrawals = (account.withdrawals || []).map((w) => ({
          ...w,
          destination: w.destination_type || "",
        }));
        setFormData({ withdrawals: mappedWithdrawals });
      } else if (selectedField === "deposits") {
        setFormData(account.deposits?.[0] || {});
      } else if (selectedField === "risk_tolerance") {
        const base = account.risk_tolerance || {};
        const seeded = { ...base };
        // No longer seeding defaults here to ensure unfilled fields look unfilled in the modal
        setFormData(seeded);
      } else if (selectedField === "transfer_info") {
        setFormData(account.transfer_info || {});
      } else if (selectedField === "trust_info") {
        const t = account.trust_info || {};
        const trusteesNorm = normalizeTrusteesForForm(t.trustees);
        setFormData({
          ...t,
          ssn_or_tin:
            t.ssn_or_tin != null && String(t.ssn_or_tin).trim() !== ""
              ? formatSSNInput(String(t.ssn_or_tin))
              : (t.ssn_or_tin ?? ""),
          trustees:
            trusteesNorm.length > 0
              ? trusteesNorm
              : [{ id: "", relationship_to_grantor: "" }],
        });
      } else if (
        selectedField === "bank_details" ||
        selectedField === "bank_accounts"
      ) {
        const allIssues = resolveAccountBankIssues(
          account,
          household?.missing_fields,
          household,
        );
        const scopedIssues = bankAccountId
          ? allIssues.filter((i) => i.bank?.id === bankAccountId)
          : allIssues;
        const incomplete = filterBankIssuesByReason(scopedIssues, "missing_data");
        const banksSeed = {};
        (incomplete || []).forEach(({ bank }) => {
          if (bank?.id) {
            banksSeed[bank.id] = {
              ...bank,
              account_type: canonicalBankAccountType(bank.account_type),
            };
          }
        });
        const linkedCount = getAccountBanks(account).length;
        const unlinked = getUnlinkedHouseholdBanks(household, account);
        const needsNewBank = linkedCount === 0 && incomplete.length === 0;
        setFormData({
          banks: banksSeed,
          bankAddMode:
            needsNewBank && unlinked.length > 0 ? "link" : "create",
          newBank: {
            bank_name: "",
            account_name: "",
            account_number: "",
            routing_number: "",
            account_type: "",
            link_type: "",
            is_personal: true,
          },
          linkBankId: unlinked[0]?.id || "",
        });
      } else if (selectedField === "basic_details") {
        setFormData({
          custodian: account.custodian,
          registration: account.registration,
          type: account.type,
          proceeds: account.proceeds,
          security_purchases: account.security_purchases,
          distribution_income: account.distribution_income,
          margin: account.margin,
          options: account.options,
          fee_based_account: account.fee_based_account,
          account_value: account.account_value,
        });
      } else if (
        selectedField === "beneficiaries_primary_allocation" ||
        selectedField === "beneficiaries_contingent_allocation"
      ) {
        const raw = account.beneficiaries || [];
        setFormData({
          beneficiaries: JSON.parse(
            JSON.stringify(Array.isArray(raw) ? raw : []),
          ),
        });
      } else if (selectedField === "beneficiaries") {
        const raw = account.beneficiaries || [];
        setFormData({
          beneficiaries: JSON.parse(
            JSON.stringify(Array.isArray(raw) ? raw : []),
          ),
        });
      } else if (selectedField === "trusted_contacts") {
        const raw = account.trusted_contacts || [];
        setFormData({
          trusted_contacts: JSON.parse(
            JSON.stringify(Array.isArray(raw) ? raw : []),
          ),
        });
      } else if (selectedField === "business_info") {
        const biz = account.business_info || {};
        const addr = biz.address || {};
        setFormData({
          business_name: biz.business_name || "",
          ssn:
            biz.ssn != null && String(biz.ssn).trim() !== ""
              ? formatSSNInput(String(biz.ssn))
              : "",
          is_operating:
            biz.is_operating === true || biz.is_operating === false
              ? String(biz.is_operating)
              : "true",
          tax_classification: biz.tax_classification || "",
          control_persons: Array.isArray(biz.control_persons)
            ? biz.control_persons.filter((c) => c && c.id)
            : [],
          address: {
            address_line_1: addr.address_line_1 || "",
            address_line_2: addr.address_line_2 || "",
            country: addr.country || "USA",
            province: addr.province || "",
            city: addr.city || "",
            postal_code: addr.postal_code || "",
            phone: addr.phone ? formatPhoneInput(String(addr.phone)) : "",
          },
        });
      } else if (selectedField === "inheritance_info") {
        const dec = account.inheritance_info?.decedent_info || {};
        setFormData({
          decedent_id: dec.id || "",
          relationship: dec.relationship || "",
        });
      } else if (selectedField === "minor_details") {
        setFormData({
          minor_member_id: "",
        });
      }
    } else {
      setFormData({});
      setSaveError("");
    }
  }, [isOpen, selectedField, account, bankAccountId]);

  const handleFieldChange = useCallback((fieldPath, value) => {
    let sanitizedValue = value;

    // Pattern-based sanitation
    if (fieldPath.includes("phone")) {
      sanitizedValue = formatPhoneInput(value);
    } else if (fieldPath.includes("postal_code")) {
      sanitizedValue = formatZipInput(value);
    } else if (
      fieldPath === "ssn_or_tin" ||
      fieldPath.endsWith(".ssn_or_tin")
    ) {
      sanitizedValue = formatSSNInput(value);
    } else if (typeof sanitizedValue === "string") {
      const isLiquiditySelectField =
        fieldPath === "liquidity_needs" ||
        fieldPath === "liquidity_requirements.amount" ||
        fieldPath === "liquidity_requirements.time_horizon";
      // Numerical sanitation for specific fields
      const isDecimalField =
        [
          "account_value",
          "federal_tax_withholding",
          "state_tax_withholding",
          "amount",
        ].some((key) => fieldPath.includes(key)) ||
        (fieldPath.includes("beneficiaries") && fieldPath.includes("share"));
      const isRoutingNumber = fieldPath.includes("routing_number");
      const isAccountNumber = !isRoutingNumber && fieldPath.includes("account_number");

      if (isLiquiditySelectField) {
        // Keep dropdown labels intact (e.g. "$50,000-99,999")
      } else if (isDecimalField) {
        sanitizedValue = formatNumericInput(value, true);
      } else if (isRoutingNumber) {
        sanitizedValue = formatRoutingNumberInput(value);
      } else if (isAccountNumber) {
        sanitizedValue = formatAccountNumberInput(value);
      }
    }

    setFormData((prev) => {
      // Auto-fill logic for Investment Strategy
      if (fieldPath === "invest_objectives") {
        const objective = sanitizedValue;
        const matchingKey = Object.keys(BFS_MAPPING).find(
          (key) => BFS_MAPPING[key].invest_objectives?.[0] === objective,
        );

        if (matchingKey) {
          const defaults = BFS_MAPPING[matchingKey];
          const rawSources = Array.isArray(defaults.source_of_funds)
            ? defaults.source_of_funds
            : [defaults.source_of_funds];
          return {
            ...prev,
            invest_objectives: [objective],
            risk_tolerance: defaults.risk_tolerance,
            decision_making: defaults.decision_making,
            source_of_funds: rawSources,
            use_of_funds: defaults.use_of_funds,
            time_horizon: defaults.time_horizon,
          };
        }
      }

      if (fieldPath === "time_horizon" && typeof sanitizedValue === "string") {
        return {
          ...prev,
          time_horizon: parseAccountTimeHorizon(sanitizedValue),
        };
      }

      if (
        (fieldPath === "liquidity_needs" ||
          fieldPath === "liquidity_requirements.amount") &&
        typeof sanitizedValue === "string"
      ) {
        return {
          ...prev,
          liquidity_requirements: {
            ...(prev.liquidity_requirements || {}),
            amount: sanitizedValue ? parseLiquidityNeedsDisplay(sanitizedValue) : null,
          },
        };
      }

      if (
        fieldPath === "liquidity_requirements.time_horizon" &&
        typeof sanitizedValue === "string"
      ) {
        return {
          ...prev,
          liquidity_requirements: {
            ...(prev.liquidity_requirements || {}),
            time_horizon: sanitizedValue
              ? parseLiquidityTimeHorizonDisplay(sanitizedValue)
              : null,
          },
        };
      }

      const keys = fieldPath.split(".");

      const updateRecursive = (obj, pathKeys, val) => {
        const key = pathKeys[0];
        if (pathKeys.length === 1) {
          if (Array.isArray(obj)) {
            const newArr = [...obj];
            newArr[parseInt(key)] = val;
            return newArr;
          }
          return { ...obj, [key]: val };
        }

        const nextTarget = obj[key] || (isNaN(pathKeys[1]) ? {} : []);
        const updatedValue = updateRecursive(
          nextTarget,
          pathKeys.slice(1),
          val,
        );

        if (Array.isArray(obj)) {
          const newArr = [...obj];
          newArr[parseInt(key)] = updatedValue;
          return newArr;
        }
        return { ...obj, [key]: updatedValue };
      };

      return updateRecursive(prev, keys, sanitizedValue);
    });
  }, []);

  const getFormValue = useCallback(
    (fieldPath, fallback) => {
      if (!fieldPath) return fallback;
      const keys = fieldPath.split(".");
      let current = formData;
      for (const key of keys) {
        if (current && Object.prototype.hasOwnProperty.call(current, key)) {
          current = current[key];
        } else {
          return fallback;
        }
      }
      return current !== undefined ? current : fallback;
    },
    [formData],
  );

  // Get all household members for dropdowns
  const getAllMembers = () => {
    const members = [];
    if (household?.members) {
      household.members.forEach((member) => {
        const name =
          `${member.personal_info?.first_name || ""} ${member.personal_info?.last_name || ""}`.trim();
        if (name) {
          members.push({ id: member.id, name });
        }
      });
    }
    return members;
  };

  const getAllAccounts = () => {
    return household?.accounts?.filter((acc) => acc.id !== account?.id) || [];
  };

  const getAllBankAccounts = () => {
    return household?.bank_accounts || [];
  };

  const getDuplicateMemberIds = (rows, getMemberId) => {
    const seen = new Set();
    const dups = new Set();
    (rows || []).forEach((row, idx) => {
      const raw = getMemberId(row, idx);
      if (raw === null || raw === undefined || raw === "") return;
      const id = String(raw);
      if (seen.has(id)) dups.add(id);
      else seen.add(id);
    });
    return Array.from(dups);
  };

  const handleSave = async () => {
    if (!account || !selectedField) return;

    const field = selectedField;
    if (field === "trust_info") {
      const revocableCurrent = isRevocableTrust(account.type);
      const ex =
        getFormValue("execution_date", "") ||
        account.trust_info?.execution_date;
      if (ex) {
        const msg = validatePastOnlyYmd(ex, "Execution date");
        if (msg) {
          toast.error(msg);
          return;
        }
      }
      const am = getFormValue("amended_date", "");
      if (am) {
        const msg = validatePastOnlyYmd(am, "Amended date");
        if (msg) {
          toast.error(msg);
          return;
        }
      }
      const tinRaw = getFormValue("ssn_or_tin", "");
      if (!revocableCurrent) {
        const tinError = getTaxIdCountError(tinRaw);
        if (tinError) {
          toast.error(tinError);
          return;
        }
      }
      const trusteeRowsEarly = getFormValue("trustees", formData.trustees || []);
      if (hasDuplicateTrusteeMemberIds(trusteeRowsEarly)) {
        toast.error("Each trustee must be a different member.");
        return;
      }
    }
    if (field === "business_info") {
      const bizSsnCount = countSSNDigits(getFormValue("ssn", ""));
      if (bizSsnCount > 0 && bizSsnCount !== 9) {
        toast.error("Business Tax ID must be 9 digits.");
        return;
      }
      const bizPostal = getFormValue("address.postal_code", "");
      if (bizPostal.trim() !== "" && !validateZipDigits(bizPostal)) {
        toast.error("Business address postal code must be 5 digits.");
        return;
      }
      const bizPhone = getFormValue("address.phone", "");
      if (bizPhone.trim() !== "" && !validatePhoneDigits(bizPhone)) {
        toast.error("Business phone must be 10 digits.");
        return;
      }
    }
    if (field === "minor_details") {
      const existingOwnerIds = (Array.isArray(account.owners_info) ? account.owners_info : [])
        .filter((o) => o && typeof o === "object" && o.id)
        .map((o) => String(o.id));
      const minorId = getFormValue("minor_member_id", "");
      if (minorId && existingOwnerIds.includes(String(minorId))) {
        toast.error("The selected member is already an owner of this account.");
        return;
      }
    }
    if (field === "deposits") {
      const sd = getFormValue("start_date", "");
      if (sd) {
        const msg = validateScheduleFutureYmd(sd, "Start date");
        if (msg) {
          toast.error(msg);
          return;
        }
      }
    }
    if (field === "beneficiaries") {
      const list = formData.beneficiaries || account.beneficiaries || [];
      const dupIds = getDuplicateMemberIds(list, (b, i) =>
        getFormValue(
          `beneficiaries.${i}.household_member_id`,
          b?.household_member_id,
        ),
      );
      if (dupIds.length > 0) {
        setSaveError(
          "Duplicate beneficiaries detected. Each household member can only appear once in beneficiaries.",
        );
        return;
      }
      let primaryTotal = 0;
      let contingentTotal = 0;
      for (let i = 0; i < list.length; i++) {
        const b = list[i];
        const t = String(
          getFormValue(`beneficiaries.${i}.type`, b?.type) || "",
        ).toLowerCase();
        const sh = parseFloat(
          getFormValue(`beneficiaries.${i}.share`, b?.share),
        );
        const n = Number.isFinite(sh) ? sh : 0;
        if (t === "primary") primaryTotal += n;
        if (t === "contingent") contingentTotal += n;
      }
      if (isBeneficiaryShareTotalOver100(primaryTotal)) {
        toast.error(`Primary allocations exceed 100% (${Math.round(primaryTotal * 100) / 100}%).`);
        return;
      }
      if (isBeneficiaryShareTotalOver100(contingentTotal)) {
        toast.error(`Contingent allocations exceed 100% (${Math.round(contingentTotal * 100) / 100}%).`);
        return;
      }
    }
    if (field === "trusted_contacts") {
      const list = formData.trusted_contacts || account.trusted_contacts || [];
      const dupIds = getDuplicateMemberIds(list, (tc, i) =>
        getFormValue(
          `trusted_contacts.${i}.household_member_id`,
          tc?.household_member_id,
        ),
      );
      if (dupIds.length > 0) {
        setSaveError(
          "Duplicate trusted contacts detected. Each household member can only appear once in trusted contacts.",
        );
        return;
      }
    }
    if (
      field === "beneficiaries_primary_allocation" ||
      field === "beneficiaries_contingent_allocation"
    ) {
      const typeKey =
        field === "beneficiaries_primary_allocation" ? "primary" : "contingent";
      const list = formData.beneficiaries || account.beneficiaries || [];
      const dupIds = getDuplicateMemberIds(list, (b) => b?.household_member_id);
      if (dupIds.length > 0) {
        setSaveError(
          "Duplicate beneficiaries detected. Each household member can only appear once in beneficiaries.",
        );
        return;
      }
      let total = 0;
      list.forEach((b, i) => {
        if (String(b?.type || "").toLowerCase() !== typeKey) return;
        const sh = parseFloat(
          getFormValue(`beneficiaries.${i}.share`, b?.share),
        );
        total += Number.isFinite(sh) ? sh : 0;
      });
      if (isBeneficiaryShareTotalOver100(total)) {
        toast.error(`${typeKey === "primary" ? "Primary" : "Contingent"} allocations exceed 100% (${Math.round(total * 100) / 100}%).`);
        return;
      }
    }

    if (field === "withdrawals") {
      const list = formData.withdrawals || [];
      for (let i = 0; i < list.length; i++) {
        const sd = getFormValue(
          `withdrawals.${i}.start_date`,
          list[i]?.start_date,
        );
        if (sd) {
          const msg = validateScheduleFutureYmd(sd, "Start date");
          if (msg) {
            toast.error(msg);
            return;
          }
        }
      }
    }

    if (field === "transfer_info") {
      const oldAccountNumber = getFormValue(
        "old_account_number",
        account.transfer_info?.old_account_number || "",
      );
      const deliveringFirm = getFormValue(
        "delivering_firm_name",
        account.transfer_info?.delivering_firm_name || "",
      );
      const lpl = validateLplAccountNumber(oldAccountNumber, deliveringFirm);
      if (!lpl.valid) {
        setSaveError(lpl.error);
        return;
      }
    }

    if (field === "bank_details" || field === "bank_accounts") {
      const newBankRouting = getFormValue("newBank.routing_number", "");
      const hasInvalidRouting =
        Object.entries(formData.banks || {}).some(([bId, b]) => {
          const rv = getFormValue(
            `banks.${bId}.routing_number`,
            b.routing_number || "",
          );
          return String(rv).trim() !== "" && !validateRoutingNumber(rv);
        }) ||
        (String(newBankRouting).trim() !== "" &&
          !validateRoutingNumber(newBankRouting));
      if (hasInvalidRouting) {
        setSaveError("Enter a valid 9-digit routing number");
        return;
      }
    }

    setSaving(true);
    try {
      if (
        (field === "bank_details" || field === "bank_accounts") &&
        bankDocSelectedFile
      ) {
        const uploaded = await handleBankDocUpload(bankDocSelectedFile);
        if (!uploaded) return;
      }

      const updatePayload = {};

      // Handle basic_details
      if (field === "basic_details") {
        if (formData.custodian !== undefined) {
          updatePayload.custodian = formData.custodian || null;
        }
        if (formData.registration !== undefined) {
          updatePayload.registration = formData.registration || null;
        }
        if (formData.type !== undefined) {
          updatePayload.type = formData.type || null;
        }
        if (formData.proceeds !== undefined) {
          updatePayload.proceeds = formData.proceeds || null;
        }
        if (formData.security_purchases !== undefined) {
          updatePayload.security_purchases = formData.security_purchases || null;
        }
        if (formData.distribution_income !== undefined) {
          updatePayload.distribution_income = formData.distribution_income || null;
        }
        if (formData.margin !== undefined && formData.margin !== null) {
          updatePayload.margin = typeof formData.margin === "boolean" ? formData.margin : null;
        }
        if (formData.options !== undefined && formData.options !== null) {
          updatePayload.options = typeof formData.options === "boolean" ? formData.options : null;
        }
        if (formData.fee_based_account !== undefined && formData.fee_based_account !== null) {
          updatePayload.fee_based_account = typeof formData.fee_based_account === "boolean" ? formData.fee_based_account : null;
        }
        if (formData.account_value !== undefined) {
          const parsedAccountValue =
            formData.account_value !== "" && formData.account_value !== null
              ? parseFloat(formData.account_value)
              : null;
          updatePayload.account_value =
            parsedAccountValue !== null &&
            !Number.isNaN(parsedAccountValue) &&
            parsedAccountValue >= 0
              ? parsedAccountValue
              : null;
        }
      }

      // Handle risk_tolerance (Investment Strategy)
      if (field === "risk_tolerance") {
        const baseRiskTolerance = account.risk_tolerance || {};
        const riskTolerance = { ...baseRiskTolerance };
        const toRangeObject = (value, parseDisplay) => {
          if (value === undefined) return undefined;
          if (value === null || value === "") return null;
          if (typeof value === "string") {
            return typeof parseDisplay === "function"
              ? parseDisplay(value)
              : null;
          }
          if (
            typeof value === "object" &&
            value.start !== undefined &&
            value.end !== undefined
          ) {
            return {
              start: parseInt(value.start, 10),
              end: parseInt(value.end, 10),
            };
          }
          return null;
        };

        if (formData.invest_objectives !== undefined) {
          const objectives = Array.isArray(formData.invest_objectives)
            ? formData.invest_objectives
            : formData.invest_objectives
              ? [formData.invest_objectives]
              : [];
          riskTolerance.invest_objectives = objectives.filter((o) => o);
        } else if (
          baseRiskTolerance.invest_objectives &&
          baseRiskTolerance.invest_objectives.length > 0
        ) {
          riskTolerance.invest_objectives = baseRiskTolerance.invest_objectives;
        } else {
          riskTolerance.invest_objectives = [PRIMARY_OBJECTIVE_OPTIONS[0]];
        }

        if (formData.risk_tolerance !== undefined) {
          riskTolerance.risk_tolerance = formData.risk_tolerance || null;
        } else if (baseRiskTolerance.risk_tolerance !== undefined) {
          riskTolerance.risk_tolerance = baseRiskTolerance.risk_tolerance;
        } else {
          riskTolerance.risk_tolerance = null;
        }

        if (formData.decision_making !== undefined) {
          riskTolerance.decision_making = formData.decision_making || null;
        } else if (baseRiskTolerance.decision_making !== undefined) {
          riskTolerance.decision_making = baseRiskTolerance.decision_making;
        } else {
          riskTolerance.decision_making = null;
        }

        if (formData.source_of_funds !== undefined) {
          const sources = Array.isArray(formData.source_of_funds)
            ? formData.source_of_funds
            : formData.source_of_funds
              ? [formData.source_of_funds]
              : [];
          riskTolerance.source_of_funds =
            sources.length > 0 ? sources : ["Income"];
        } else if (
          baseRiskTolerance.source_of_funds &&
          baseRiskTolerance.source_of_funds.length > 0
        ) {
          riskTolerance.source_of_funds = baseRiskTolerance.source_of_funds;
        } else {
          riskTolerance.source_of_funds = ["Income"];
        }

        if (formData.use_of_funds !== undefined) {
          riskTolerance.use_of_funds = formData.use_of_funds || null;
        } else if (
          baseRiskTolerance.use_of_funds !== undefined &&
          baseRiskTolerance.use_of_funds !== null
        ) {
          riskTolerance.use_of_funds = baseRiskTolerance.use_of_funds;
        } else {
          riskTolerance.use_of_funds = "Retirement Savings";
        }

        if (formData.time_horizon !== undefined) {
          riskTolerance.time_horizon = formData.time_horizon || null;
        } else if (baseRiskTolerance.time_horizon !== undefined) {
          riskTolerance.time_horizon = baseRiskTolerance.time_horizon;
        } else {
          riskTolerance.time_horizon = null;
        }

        if (
          formData.liquidity_requirements?.amount !== undefined ||
          formData.liquidity_requirements?.time_horizon !== undefined
        ) {
          const liquidityRequirements = {
            ...(baseRiskTolerance.liquidity_requirements || {}),
          };

          if (formData.liquidity_requirements?.amount !== undefined) {
            liquidityRequirements.amount = toRangeObject(
              formData.liquidity_requirements.amount,
              parseLiquidityNeedsDisplay,
            );
          }
          if (formData.liquidity_requirements?.time_horizon !== undefined) {
            liquidityRequirements.time_horizon = toRangeObject(
              formData.liquidity_requirements.time_horizon,
              parseLiquidityTimeHorizonDisplay,
            );
          }

          riskTolerance.liquidity_requirements = liquidityRequirements;
        } else if (baseRiskTolerance.liquidity_requirements !== undefined) {
          riskTolerance.liquidity_requirements =
            baseRiskTolerance.liquidity_requirements;
        }

        updatePayload.risk_tolerance = riskTolerance;
      }

      // Handle transfer_info
      if (field === "transfer_info") {
        const baseTransferInfo = account.transfer_info || {};
        const transferInfo = { ...baseTransferInfo };

        if (formData.old_account_number !== undefined) {
          transferInfo.old_account_number = formData.old_account_number || null;
        } else if (baseTransferInfo.old_account_number !== undefined) {
          transferInfo.old_account_number = baseTransferInfo.old_account_number;
        }

        if (formData.delivering_firm_name !== undefined) {
          transferInfo.delivering_firm_name =
            formData.delivering_firm_name || null;
        } else if (baseTransferInfo.delivering_firm_name !== undefined) {
          transferInfo.delivering_firm_name =
            baseTransferInfo.delivering_firm_name;
        }

        if (formData.is_full_transfer !== undefined) {
          transferInfo.is_full_transfer = formData.is_full_transfer !== false; // default true
        } else if (baseTransferInfo.is_full_transfer !== undefined) {
          transferInfo.is_full_transfer = baseTransferInfo.is_full_transfer;
        } else {
          transferInfo.is_full_transfer = true; // default true
        }

        if (formData.transfer_type !== undefined) {
          transferInfo.transfer_type = formData.transfer_type || null;
        } else if (baseTransferInfo.transfer_type !== undefined) {
          transferInfo.transfer_type = baseTransferInfo.transfer_type;
        } else {
          transferInfo.transfer_type = "Brokerage or Trust Company";
        }

        // Handle address - merge existing address with form data
        const baseAddress = baseTransferInfo.address || {};
        const address = {
          ...baseAddress,
          type: baseAddress.type || "work",
        };

        if (formData.address) {
          const addressFields = [
            "address_line_1",
            "address_line_2",
            "city",
            "state",
            "postal_code",
            "country",
            "phone",
          ];
          addressFields.forEach((field) => {
            if (formData.address[field] !== undefined) {
              address[field] = formData.address[field] || null;
            } else if (baseAddress[field] !== undefined) {
              address[field] = baseAddress[field];
            }
          });

          // Ensure country defaults to "USA" if blank or "United States"
          if (!address.country || address.country.trim() === "" || address.country === "United States") {
            address.country = "USA";
          }
        }

        // Only include address if it has at least one field
        const hasAddressFields = Object.keys(address).some(
          (key) =>
            key !== "type" &&
            address[key] !== null &&
            address[key] !== undefined &&
            address[key] !== "",
        );
        if (hasAddressFields || baseAddress) {
          transferInfo.address = address;
        }

        updatePayload.transfer_info = transferInfo;
      }

      // Handle trust_info
      if (field === "trust_info") {
        const baseTrustInfo = account.trust_info || {};
        const trustInfo = { ...baseTrustInfo };
        delete trustInfo.revocable;
        const currentRevocable = isRevocableTrust(account.type);
        const resolvedGrantorIds =
          formData.grantorIds !== undefined
            ? formData.grantorIds
            : getTrustGrantorIds(baseTrustInfo);
        const primaryGrantorId = resolvedGrantorIds[0] || "";
        const grantorDigits = getMemberSsnDigitsById(
          primaryGrantorId,
          household?.members || [],
        );

        if (formData.trust_name !== undefined) {
          const tn = formData.trust_name;
          trustInfo.trust_name =
            typeof tn === "string" && tn.trim() !== "" ? tn.trim() : null;
        } else if (baseTrustInfo.trust_name !== undefined) {
          trustInfo.trust_name = baseTrustInfo.trust_name;
        }

        if (formData.trust_title !== undefined) {
          trustInfo.trust_title = formData.trust_title || null;
        } else if (baseTrustInfo.trust_title !== undefined) {
          trustInfo.trust_title = baseTrustInfo.trust_title;
        }

        if (currentRevocable) {
          trustInfo.ssn_or_tin = grantorDigits.length === 9 ? grantorDigits : null;
        } else if (formData.ssn_or_tin !== undefined) {
          const raw = String(formData.ssn_or_tin || "");
          if (raw.includes("*")) {
            trustInfo.ssn_or_tin = baseTrustInfo.ssn_or_tin ?? null;
          } else {
            const d = raw.replace(/\D/g, "");
            trustInfo.ssn_or_tin = d.length === 9 ? d : null;
          }
        } else if (baseTrustInfo.ssn_or_tin !== undefined) {
          trustInfo.ssn_or_tin = baseTrustInfo.ssn_or_tin;
        }

        if (formData.trust_state !== undefined) {
          trustInfo.trust_state = formData.trust_state || null;
        } else if (baseTrustInfo.trust_state !== undefined) {
          trustInfo.trust_state = baseTrustInfo.trust_state;
        }

        if (formData.execution_date !== undefined) {
          trustInfo.execution_date = formData.execution_date || null;
        } else if (baseTrustInfo.execution_date !== undefined) {
          trustInfo.execution_date = baseTrustInfo.execution_date;
        }

        if (formData.amended_date !== undefined) {
          trustInfo.amended_date = formData.amended_date || null;
        } else if (baseTrustInfo.amended_date !== undefined) {
          trustInfo.amended_date = baseTrustInfo.amended_date;
        }

        if (formData.trustees !== undefined) {
          trustInfo.trustees = normalizeTrusteesPayload(
            formData.trustees,
            primaryGrantorId || null,
          );
        } else if (baseTrustInfo.trustees) {
          trustInfo.trustees = normalizeTrusteesPayload(
            baseTrustInfo.trustees,
            primaryGrantorId || null,
          );
        }

        // Build the final trust_info payload using buildTrustInfoUpdatePayload so that
        // grantors[] is written and grantor_or_settler is never sent to the API.
        const patch = { ...trustInfo };
        delete patch.grantors;
        delete patch.grantor_or_settler;
        updatePayload.trust_info = buildTrustInfoUpdatePayload(baseTrustInfo, {
          grantorIds: resolvedGrantorIds,
          patch,
        });
      }

      // Handle business_info
      if (field === "business_info") {
        const baseBiz = account.business_info || {};
        const baseAddr = baseBiz.address || {};
        const taxOptions = getTaxClassificationOptions(account.type);
        const baseControlPersons = Array.isArray(baseBiz.control_persons)
          ? baseBiz.control_persons.filter((c) => c && c.id)
          : [];
        const wasAddressGroupBlank =
          isBlank(baseAddr.address_line_1) ||
          isBlank(baseAddr.city) ||
          isBlank(baseAddr.province) ||
          isBlank(baseAddr.postal_code) ||
          isBlank(baseAddr.country) ||
          isBlank(baseAddr.phone);

        const businessNameVal = isBlank(baseBiz.business_name)
          ? getFormValue("business_name", "").trim() || null
          : baseBiz.business_name;

        const taxClassificationVal =
          taxOptions.length > 0 && isBlank(baseBiz.tax_classification)
            ? getFormValue("tax_classification", "") || null
            : (baseBiz.tax_classification ?? null);

        const isOperatingVal =
          baseBiz.is_operating !== true && baseBiz.is_operating !== false
            ? getFormValue("is_operating", "") !== ""
              ? getFormValue("is_operating", "") === "true"
              : null
            : baseBiz.is_operating;

        const isNonProfitVal =
          getFormValue("is_non_profit", baseBiz.is_non_profit === true) === true;

        const controlPersonsVal =
          baseControlPersons.length === 0
            ? getFormValue("control_persons", []).filter((c) => c && c.id)
            : baseControlPersons;

        const ssnVal = isBlank(baseBiz.ssn)
          ? String(getFormValue("ssn", "")).replace(/\D/g, "") || null
          : baseBiz.ssn;

        updatePayload.business_info = {
          business_name: businessNameVal,
          tax_classification: taxOptions.includes(taxClassificationVal)
            ? taxClassificationVal
            : null,
          is_operating: isOperatingVal,
          is_non_profit: isNonProfitVal,
          address: {
            ...baseAddr,
            address_line_1: wasAddressGroupBlank
              ? getFormValue("address.address_line_1", "") ||
                baseAddr.address_line_1 ||
                null
              : (baseAddr.address_line_1 ?? null),
            address_line_2: wasAddressGroupBlank
              ? getFormValue("address.address_line_2", "") ||
                baseAddr.address_line_2 ||
                null
              : (baseAddr.address_line_2 ?? null),
            country: wasAddressGroupBlank
              ? getFormValue("address.country", "") || null
              : (baseAddr.country ?? null),
            province: wasAddressGroupBlank
              ? getFormValue("address.province", "") || baseAddr.province || null
              : (baseAddr.province ?? null),
            city: wasAddressGroupBlank
              ? getFormValue("address.city", "") || baseAddr.city || null
              : (baseAddr.city ?? null),
            postal_code: wasAddressGroupBlank
              ? getFormValue("address.postal_code", "") ||
                baseAddr.postal_code ||
                null
              : (baseAddr.postal_code ?? null),
            phone: wasAddressGroupBlank
              ? String(getFormValue("address.phone", "")).replace(/\D/g, "") ||
                (baseAddr.phone
                  ? String(baseAddr.phone).replace(/\D/g, "")
                  : null)
              : baseAddr.phone
                ? String(baseAddr.phone).replace(/\D/g, "")
                : null,
          },
          control_persons: controlPersonsVal,
          ssn: ssnVal,
          notes: baseBiz.notes ?? null,
        };
      }

      // Handle inheritance_info
      if (field === "inheritance_info") {
        const baseDec = account.inheritance_info?.decedent_info || {};
        const decIdVal = isBlank(baseDec.id)
          ? getFormValue("decedent_id", "")
          : baseDec.id;
        if (!isBlank(decIdVal)) {
          const relationshipVal = isBlank(baseDec.relationship)
            ? getFormValue("relationship", "") || null
            : baseDec.relationship;

          updatePayload.inheritance_info = {
            decedent_info: {
              id: decIdVal,
              relationship: relationshipVal,
            },
          };
        }
      }

      if (field === "minor_details") {
        const minorId = getFormValue("minor_member_id", "");
        if (!isBlank(minorId)) {
          const existingOwners = (Array.isArray(account.owners_info) ? account.owners_info : [])
            .filter((o) => o && typeof o === "object" && o.id);
          const allOwners = [...existingOwners, { id: minorId, type: "primary" }];
          const evenShare = Math.floor(100 / allOwners.length);
          updatePayload.owners_info = allOwners.map((owner, idx) => ({
            ...owner,
            share: idx === allOwners.length - 1
              ? 100 - evenShare * (allOwners.length - 1)
              : evenShare,
          }));
        }
      }

      if (field === "bank_details" || field === "bank_accounts") {
        const accountBanks = Array.isArray(account.bank_accounts)
          ? account.bank_accounts
          : [];
        const linkedCount = accountBanks.length;
        const banksForm = (formData && formData.banks) || {};
        const hasIncomplete = Object.keys(banksForm).length > 0;

        if (linkedCount === 0 && !hasIncomplete) {
          // Account has no linked bank — user is creating new or linking an existing household bank.
          const mode = formData?.bankAddMode || "create";

          if (mode === "link") {
            const linkId = formData?.linkBankId || "";
            if (linkId) {
              if (clientToken && household_id) {
                updatePayload.bank_accounts = { link: [linkId] };
              } else {
                updatePayload.add_bank_accounts = [linkId];
              }
            }
          } else {
            const nb = formData?.newBank || {};
            const required = [
              "bank_name",
              "account_name",
              "account_number",
              "routing_number",
              "account_type",
            ];
            const missing = required.filter((k) => {
              const v = nb[k];
              return v == null || (typeof v === "string" && v.trim() === "");
            });
            if (missing.length === 0) {
              const created = {};
              BANK_TRACKED_KEYS.forEach((key) => {
                const raw = nb[key];
                created[key] =
                  key === "is_personal"
                    ? (raw ?? true)
                    : typeof raw === "string"
                      ? raw.trim() || null
                      : (raw ?? null);
              });
              if (clientToken && household_id) {
                updatePayload.bank_accounts = { create: [created] };
              } else {
                updatePayload.bank_accounts = [created];
              }
            }
          }
        } else {
          const diffsByBank = {};

          Object.keys(banksForm).forEach((baId) => {
            const base = accountBanks.find((b) => b && b.id === baId) || {};
            const edited = banksForm[baId] || {};
            const diff = {};

            BANK_TRACKED_KEYS.forEach((key) => {
              if (!Object.prototype.hasOwnProperty.call(edited, key)) return;
              const raw = edited[key];
              const normalized =
                key === "is_personal"
                  ? raw
                  : typeof raw === "string"
                    ? raw || null
                    : (raw ?? null);
              if (normalized !== base[key]) {
                diff[key] = normalized;
              }
            });

            if (
              !Object.prototype.hasOwnProperty.call(diff, "is_personal") &&
              (base.is_personal === null || base.is_personal === undefined)
            ) {
              diff.is_personal = true;
            }

            if (Object.keys(diff).length > 0) {
              diffsByBank[baId] = diff;
            }
          });

          if (Object.keys(diffsByBank).length > 0) {
            if (clientToken && household_id) {
              updatePayload.bank_accounts = { update: diffsByBank };
            } else {
              updatePayload.bank_accounts = Object.entries(diffsByBank).map(
                ([id, d]) => ({ id, ...d }),
              );
            }
          }
        }
      }

      // Handle withdrawals
      if (field === "withdrawals") {
        const withdrawals = (formData.withdrawals || []).map((w) => {
          const withdrawal = { ...w };
          // Ensure numeric fields are parsed
          if (withdrawal.federal_tax_withholding !== undefined) {
            withdrawal.federal_tax_withholding =
              withdrawal.federal_tax_withholding !== null &&
              withdrawal.federal_tax_withholding !== ""
                ? parseFloat(withdrawal.federal_tax_withholding)
                : null;
          }
          if (withdrawal.state_tax_withholding !== undefined) {
            withdrawal.state_tax_withholding =
              withdrawal.state_tax_withholding !== null &&
              withdrawal.state_tax_withholding !== ""
                ? parseFloat(withdrawal.state_tax_withholding)
                : null;
          }
          if (withdrawal.amount !== undefined) {
            withdrawal.amount =
              withdrawal.amount !== null && withdrawal.amount !== ""
                ? parseFloat(withdrawal.amount)
                : null;
          }
          withdrawal.type =
            withdrawal.type === "" ? null : (withdrawal.type ?? null);
          return withdrawal;
        });

        updatePayload.withdrawals = withdrawals;
      }

      // Handle deposits
      if (field === "deposits") {
        const deposit = {};

        if (formData.amount !== undefined) {
          deposit.amount = formData.amount ? parseFloat(formData.amount) : null;
        }

        if (formData.frequency !== undefined) {
          deposit.frequency = formData.frequency || null;
        }

        if (formData.start_date !== undefined) {
          deposit.start_date = formData.start_date || null;
        }

        if (formData.type !== undefined) {
          deposit.type = formData.type || null;
        }

        if (formData.method !== undefined) {
          deposit.method = formData.method || null;
        }

        if (Object.keys(deposit).length > 0) {
          updatePayload.deposits = [deposit];
        }
      }

      if (
        field === "beneficiaries_primary_allocation" ||
        field === "beneficiaries_contingent_allocation"
      ) {
        const list = formData.beneficiaries || account.beneficiaries || [];
        const toUpdate = [];
        const toAdd = [];
        for (const b of list) {
          const share =
            b.share !== null && b.share !== undefined && b.share !== ""
              ? parseFloat(b.share)
              : null;
          const isNew = !b?.id || b.__isNew;
          if (isNew) {
            if (b.household_member_id) {
              toAdd.push({
                household_member_id: b.household_member_id,
                type: b.type,
                share,
                relationship: b.relationship,
              });
            }
          } else {
            toUpdate.push({
              id: b.id,
              household_member_id: b.household_member_id,
              type: b.type,
              share,
              relationship: b.relationship,
            });
          }
        }
        if (clientToken && household_id) {
          const beneficiariesPayload = {};
          if (toAdd.length > 0) {
            beneficiariesPayload.create = toAdd.map((b) => ({
              household_member_id: b.household_member_id,
              type: b.type,
              share: b.share,
              relationship: b.relationship,
            }));
          }
          if (toUpdate.length > 0) {
            beneficiariesPayload.update = {};
            for (const b of toUpdate) {
              beneficiariesPayload.update[b.id] = {
                share: b.share,
                type: b.type,
                relationship: b.relationship,
              };
            }
          }
          if (Object.keys(beneficiariesPayload).length > 0) {
            updatePayload.beneficiaries = beneficiariesPayload;
          }
        } else {
          if (toUpdate.length > 0) {
            updatePayload.beneficiaries = toUpdate;
          }
          if (toAdd.length > 0) {
            updatePayload.add_beneficiaries = toAdd;
          }
        }
      }

      if (field === "beneficiaries") {
        const list = formData.beneficiaries || account.beneficiaries || [];
        const toUpdate = [];
        const toAdd = [];
        for (let i = 0; i < list.length; i++) {
          const b = list[i];
          const household_member_id = getFormValue(
            `beneficiaries.${i}.household_member_id`,
            b?.household_member_id,
          );
          const type = getFormValue(`beneficiaries.${i}.type`, b?.type);
          const shareRaw = getFormValue(`beneficiaries.${i}.share`, b?.share);
          const share =
            shareRaw !== null && shareRaw !== undefined && shareRaw !== ""
              ? parseFloat(shareRaw)
              : null;
          const relationship = getFormValue(
            `beneficiaries.${i}.relationship`,
            b?.relationship,
          );
          const isNew = !b?.id || b.__isNew;
          if (isNew) {
            if (household_member_id) {
              toAdd.push({
                household_member_id,
                type,
                share,
                relationship,
              });
            }
          } else {
            toUpdate.push({
              id: b.id,
              household_member_id,
              type,
              share,
              relationship,
            });
          }
        }
        if (clientToken && household_id) {
          const beneficiariesPayload = {};
          if (toAdd.length > 0) {
            beneficiariesPayload.create = toAdd.map((row) => ({
              household_member_id: row.household_member_id,
              type: String(row.type || "").toLowerCase(),
              share: row.share,
              relationship: row.relationship,
            }));
          }
          if (toUpdate.length > 0) {
            beneficiariesPayload.update = {};
            for (const row of toUpdate) {
              beneficiariesPayload.update[row.id] = {
                type: row.type,
                share: row.share,
                relationship: row.relationship,
              };
            }
          }
          if (Object.keys(beneficiariesPayload).length > 0) {
            updatePayload.beneficiaries = beneficiariesPayload;
          }
        } else {
          if (toUpdate.length > 0) {
            updatePayload.beneficiaries = toUpdate;
          }
          if (toAdd.length > 0) {
            updatePayload.add_beneficiaries = toAdd;
          }
        }
      }

      if (field === "trusted_contacts") {
        const list = formData.trusted_contacts || account.trusted_contacts || [];
        const toUpdate = [];
        const toAdd = [];
        for (let i = 0; i < list.length; i++) {
          const tc = list[i];
          const household_member_id = getFormValue(
            `trusted_contacts.${i}.household_member_id`,
            tc?.household_member_id,
          );
          const relationship = getFormValue(
            `trusted_contacts.${i}.relationship`,
            tc?.relationship,
          );
          const isNew = !tc?.id || tc.__isNew;
          if (isNew) {
            if (household_member_id) {
              toAdd.push({ household_member_id, relationship });
            }
          } else {
            toUpdate.push({
              id: tc.id,
              household_member_id,
              relationship,
            });
          }
        }
        if (clientToken && household_id) {
          const tcPayload = {};
          if (toAdd.length > 0) {
            tcPayload.create = toAdd;
          }
          if (toUpdate.length > 0) {
            tcPayload.update = {};
            for (const row of toUpdate) {
              tcPayload.update[row.id] = {
                household_member_id: row.household_member_id,
                relationship: row.relationship,
              };
            }
          }
          if (Object.keys(tcPayload).length > 0) {
            updatePayload.trusted_contacts = tcPayload;
          }
        } else {
          if (toUpdate.length > 0) {
            updatePayload.trusted_contacts = toUpdate;
          }
          if (toAdd.length > 0) {
            updatePayload.add_trusted_contacts = toAdd;
          }
        }
      }

      // Only send if there are changes
      if (Object.keys(updatePayload).length > 0) {
        // Use client-side API if clientToken and household_id are provided
        if (clientToken && household_id) {
          const payload = {
            accounts: {
              [account.id]: updatePayload,
            },
          };
          const result = await updateHouseholdAPI(
            household_id,
            clientToken,
            payload,
          );

          if (result.success) {
            if (onHouseholdUpdate) {
              const fromApi = householdFromClientPutResponse(result.data);
              let merged =
                fromApi ??
                (household && account
                  ? mergeAccountIntoHouseholdForState(household, account.id, {
                      ...account,
                      ...updatePayload,
                    })
                  : null);
              if (merged && !fromApi && account && selectedField) {
                merged = stripSavedAccountMissingField(
                  merged,
                  account.id,
                  selectedField,
                );
                if (
                  selectedField === "bank_details" ||
                  selectedField === "bank_accounts"
                ) {
                  merged = stripSavedAccountMissingField(
                    merged,
                    account.id,
                    selectedField === "bank_details"
                      ? "bank_accounts"
                      : "bank_details",
                  );
                }
              }
              onHouseholdUpdate(merged ?? null);
            }
            if (onSaveAndNext) {
              onSaveAndNext();
            } else {
              onClose();
            }
          } else {
            toast.error(humanizeError(result.error, "Couldn't update account."));
          }
        } else {
          // Fallback to advisor-side API
          const response = await axiosInstance.put(
            `/households/accounts/${account.id}`,
            updatePayload,
          );
          if (onHouseholdUpdate && selectedField === "minor_details") {
            onHouseholdUpdate(null);
          } else if (onHouseholdUpdate) {
            const householdFromApi = householdFromClientPutResponse(
              response.data,
            );
            let merged;
            if (householdFromApi) {
              merged = householdFromApi;
            } else {
              merged =
                (response.data &&
                  mergeAccountIntoHouseholdForState(
                    household,
                    account.id,
                    response.data,
                  )) ||
                (household && account
                  ? mergeAccountIntoHouseholdForState(household, account.id, {
                      ...account,
                      ...updatePayload,
                    })
                  : null);
              if (merged && account && selectedField) {
                merged = stripSavedAccountMissingField(
                  merged,
                  account.id,
                  selectedField,
                );
                if (
                  selectedField === "bank_details" ||
                  selectedField === "bank_accounts"
                ) {
                  merged = stripSavedAccountMissingField(
                    merged,
                    account.id,
                    selectedField === "bank_details"
                      ? "bank_accounts"
                      : "bank_details",
                  );
                }
              }
            }
            onHouseholdUpdate(merged ?? null);
          }
          if (onSaveAndNext) {
            onSaveAndNext();
          } else {
            onClose();
          }
        }
      } else if (onSaveAndNext) {
        // Nothing to persist (e.g. a bank that only needs a document upload) —
        // still advance so Save & Next works.
        onSaveAndNext();
      } else {
        onClose();
      }
    } catch (error) {
      toast.error(humanizeError(error, "Couldn't update account."));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({});
    setSaveError("");
    onClose();
  };

  const prefillGrantorIds =
    formData.grantorIds !== undefined
      ? formData.grantorIds
      : getTrustGrantorIds(account?.trust_info);
  const prefillPrimaryGrantorId = (prefillGrantorIds || [])[0] || "";
  useRelationshipPrefillMany({
    enabled: Boolean(
      isOpen &&
        selectedField === "trust_info" &&
        account &&
        prefillPrimaryGrantorId,
    ),
    householdId: household?.id,
    counterpartyMemberId: prefillPrimaryGrantorId,
    rows: getFormValue("trustees", formData.trustees || []) || [],
    getMemberId: (row) => {
      const id = row?.id;
      if (!id || String(id) === String(prefillPrimaryGrantorId)) return null;
      return id;
    },
    getCurrentValue: (row) => row?.relationship_to_grantor,
    onPrefill: (idx, relationship) =>
      handleFieldChange(`trustees.${idx}.relationship_to_grantor`, relationship),
  });

  if (!isOpen || !selectedField || !account) return null;

  const isNonEditable = isNonEditablePendingField(selectedField);

  const pastDatePickerBounds = getDatePickerBoundsForConstraint("past_only");
  const scheduleDatePickerBounds =
    getDatePickerBoundsForConstraint("schedule_future");

  const getFieldTitle = () => {
    if (selectedField === "risk_tolerance") return "Investment Strategy";
    if (selectedField === "transfer_info") return "Transfer Details";
    if (selectedField === "trust_info") return "Trust Info";
    if (selectedField === "business_info") return "Business Info";
    if (selectedField === "inheritance_info") return "Inheritance Info";
    if (selectedField === "minor_details") return "Minor Details";
    if (selectedField === "basic_details") return "Basic Details";
    if (selectedField === "beneficiaries_primary_allocation") {
      return "Primary beneficiary allocations";
    }
    if (selectedField === "beneficiaries_contingent_allocation") {
      return "Contingent beneficiary allocations";
    }
    if (selectedField === "beneficiaries") return "Beneficiaries";
    if (selectedField === "trusted_contacts") return "Trusted Contacts";
    if (
      selectedField === "custodial_owners_both_minor" ||
      selectedField === "custodial_owner_no_minor"
    ) {
      return "Custodial Account Owners Invalid";
    }
    if (
      selectedField === "bank_details" ||
      selectedField === "bank_accounts"
    ) {
      return "Bank Account";
    }
    return selectedField
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const members = getAllMembers();

  // Dropdown options
  const frequencyOptions = FREQUENCY_OPTIONS;

  const destinationOptions = DESTINATION_TYPE_OPTIONS;

  const methodOptions = DEPOSIT_METHOD_OPTIONS;

  const allHouseholdMembers = household?.members || [];
  const accountIndex =
    allHouseholdMembers.length > 0
      ? (household?.accounts || []).findIndex((a) => a.id === account?.id)
      : -1;
  const accountLabel = account
    ? formatAccountName(account, accountIndex + 1, allHouseholdMembers)
    : null;

  const ownerFirstLabel = getAccountOwnerFirstNamesLabel(
    account,
    allHouseholdMembers,
  );

  const trustSnap = account.trust_info || {};
  const trustRevocableCurrent = isRevocableTrust(account.type);
  const trustGrantorIds =
    formData.grantorIds !== undefined
      ? formData.grantorIds
      : getTrustGrantorIds(trustSnap);
  const trustPrimaryGrantorId = trustGrantorIds[0] || "";
  const trustGrantorSsnRaw = getMemberSsnRawById(
    trustPrimaryGrantorId,
    allHouseholdMembers,
  );
  const trustGrantorSsnCount = countSSNDigits(trustGrantorSsnRaw);
  const showTrustField = {
    trust_name: isBlank(trustSnap.trust_name),
    trust_title: isBlank(trustSnap.trust_title),
    ssn_or_tin: isTrustSsnMissing(trustSnap, allHouseholdMembers, account.type),
    trust_state: isBlank(trustSnap.trust_state),
    execution_date: !trustSnap.execution_date,
    grantorIds: getTrustGrantorIds(trustSnap).length === 0,
    trustee_members: isTrustTrusteeMembersMissing(trustSnap),
    trustee_relationships: isTrustTrusteeRelationshipMissing(trustSnap),
  };
  const anyTrustFieldToShow = Object.values(showTrustField).some(Boolean);

  const bizSnap = account.business_info || {};
  const bizTaxOptions = getTaxClassificationOptions(account.type);
  const bizExistingControlPersons = Array.isArray(bizSnap.control_persons)
    ? bizSnap.control_persons.filter((c) => c && c.id)
    : [];
  const bizAddrSnap = bizSnap.address || {};
  const showBizField = {
    business_name: isBlank(bizSnap.business_name),
    ssn: isBlank(bizSnap.ssn),
    is_operating: bizSnap.is_operating !== true && bizSnap.is_operating !== false,
    tax_classification:
      bizTaxOptions.length > 0 && isBlank(bizSnap.tax_classification),
    control_persons: bizExistingControlPersons.length === 0,
    address:
      isBlank(bizAddrSnap.address_line_1) ||
      isBlank(bizAddrSnap.city) ||
      isBlank(bizAddrSnap.province) ||
      isBlank(bizAddrSnap.postal_code) ||
      isBlank(bizAddrSnap.country) ||
      isBlank(bizAddrSnap.phone),
  };
  const anyBizFieldToShow = Object.values(showBizField).some(Boolean);

  const inheritanceSnap = account.inheritance_info?.decedent_info || {};
  const showInheritanceField = {
    decedent: isBlank(inheritanceSnap.id),
    relationship: isBlank(inheritanceSnap.relationship),
  };
  const anyInheritanceFieldToShow = Object.values(showInheritanceField).some(
    Boolean,
  );

  const showBasicCustodian = isBlank(account.custodian);
  const showBasicRegistration = isBlank(account.registration);
  const showBasicType = isBlank(account.type);
  const showBasicProceeds = account.proceeds === null || account.proceeds === undefined;
  const showBasicSecurityPurchases = account.security_purchases === null || account.security_purchases === undefined;
  const showBasicDistributionIncome = account.distribution_income === null || account.distribution_income === undefined;
  const showBasicMargin = account.margin === null || account.margin === undefined;
  const showBasicOptions = account.options === null || account.options === undefined;
  const showBasicFeeBased = account.fee_based_account === null || account.fee_based_account === undefined;
  const isIws = household?.is_fee_standard !== false;
  const aumAvailability = household?.aum_availability;
  const fb = account.fee_based_account ?? (household?.is_fee_standard !== false);
  const showBasicAccountValue =
    !isFieldOptionalForIws(brokerageFlags) &&
    fb === false &&
    (aumAvailability === null ||
      aumAvailability === undefined ||
      aumAvailability === 1 ||
      aumAvailability === 2) &&
    (account.account_value == null ||
      account.account_value === "" ||
      (typeof account.account_value === "string" &&
        account.account_value.trim() === ""));
  const anyBasicFieldToShow =
    showBasicCustodian || showBasicRegistration || showBasicType || showBasicProceeds ||
    showBasicSecurityPurchases || showBasicDistributionIncome ||
    showBasicMargin || showBasicOptions || showBasicFeeBased || showBasicAccountValue;

  const isBankField =
    selectedField === "bank_details" || selectedField === "bank_accounts";
  const allBankIssues = isBankField
    ? resolveAccountBankIssues(account, household?.missing_fields, household)
    : [];
  // Scope to a single bank account when the caller opened one specific tile.
  const bankIssues = bankAccountId
    ? allBankIssues.filter((i) => i.bank?.id === bankAccountId)
    : allBankIssues;
  // Field cards only for banks missing data; missing_doc banks show the alert only.
  const incompleteBanksForRender = filterBankIssuesByReason(bankIssues, "missing_data");
  const hasBankDocMissing = bankIssues.some((i) =>
    (i.reasons || []).includes("missing_doc"),
  );
  const anyBankFieldToShow = incompleteBanksForRender.length > 0;
  const linkedBanksCount = getAccountBanks(account).length;
  const needsNewBank =
    isBankField && linkedBanksCount === 0 && !anyBankFieldToShow;
  const availableBanksForLink = isBankField
    ? getUnlinkedHouseholdBanks(household, account)
    : [];

  const depositSnap = account.deposits?.[0];
  const showDepositField = depositSnap
    ? {
        amount:
          depositSnap.amount == null ||
          depositSnap.amount === "" ||
          (typeof depositSnap.amount === "string" &&
            depositSnap.amount.trim() === ""),
        frequency: isBlank(depositSnap.frequency),
        start_date: !depositSnap.start_date,
        method: isBlank(depositSnap.method),
      }
    : {
        amount: true,
        frequency: true,
        start_date: true,
        method: true,
      };
  const anyDepositFieldToShow = Object.values(showDepositField).some(Boolean);

  const allocationType =
    selectedField === "beneficiaries_primary_allocation"
      ? "primary"
      : selectedField === "beneficiaries_contingent_allocation"
        ? "contingent"
        : null;
  const benListForAllocation =
    formData.beneficiaries ?? account.beneficiaries ?? [];
  const allocationIndices =
    allocationType != null
      ? benListForAllocation
          .map((b, i) =>
            String(b?.type || "").toLowerCase() === allocationType ? i : -1,
          )
          .filter((i) => i >= 0)
      : [];
  const allocationRunningTotal = allocationIndices.reduce((sum, i) => {
    const sh = parseFloat(
      getFormValue(`beneficiaries.${i}.share`, benListForAllocation[i]?.share),
    );
    return sum + (Number.isFinite(sh) ? sh : 0);
  }, 0);
  const allocationRemainder =
    Math.round((100 - allocationRunningTotal) * 100) / 100;

  const ownerMemberIds = new Set(
    (account?.owners_info || []).map((o) => o.id).filter(Boolean),
  );

  const addBeneficiaryRow = () => {
    if (!allocationType) return;
    setFormData((prev) => {
      const base = [...(prev.beneficiaries ?? account.beneficiaries ?? [])];
      base.push({
        __isNew: true,
        id: null,
        household_member_id: "",
        type: allocationType,
        share: 0,
      });
      return { ...prev, beneficiaries: base };
    });
  };

  const removeBeneficiaryRow = (idx) => {
    const row = benListForAllocation[idx];
    const isNew = !row?.id || row.__isNew;
    if (!isNew) return;
    setFormData((prev) => {
      const base = [...(prev.beneficiaries ?? account.beneficiaries ?? [])];
      base.splice(idx, 1);
      return { ...prev, beneficiaries: base };
    });
  };

  const getMemberDisplayName = (memberId) => {
    const m = household?.members?.find((x) => x.id === memberId);
    if (!m?.personal_info) return "Member";
    const fn = m.personal_info.first_name || "";
    const ln = m.personal_info.last_name || "";
    const name = `${fn} ${ln}`.trim();
    return name || "Member";
  };

  const benListForPending =
    formData.beneficiaries ?? account.beneficiaries ?? [];
  const tcListForPending =
    formData.trusted_contacts ?? account.trusted_contacts ?? [];

  const beneficiaryFieldMissing = (b, idx) => {
    const hm = getFormValue(
      `beneficiaries.${idx}.household_member_id`,
      b?.household_member_id,
    );
    const typ = getFormValue(`beneficiaries.${idx}.type`, b?.type);
    const shareRaw = getFormValue(`beneficiaries.${idx}.share`, b?.share);
    const rel = getFormValue(
      `beneficiaries.${idx}.relationship`,
      b?.relationship,
    );
    const shareNum = parseFloat(shareRaw);
    const shareIncomplete =
      shareRaw === null ||
      shareRaw === undefined ||
      shareRaw === "" ||
      !Number.isFinite(shareNum);
    return {
      household_member_id: isBlank(hm),
      type: isBlank(typ),
      share: shareIncomplete,
      relationship: isBlank(rel),
    };
  };

  const trustedContactFieldMissing = (tc, idx) => {
    const hm = getFormValue(
      `trusted_contacts.${idx}.household_member_id`,
      tc?.household_member_id,
    );
    const rel = getFormValue(
      `trusted_contacts.${idx}.relationship`,
      tc?.relationship,
    );
    return {
      household_member_id: isBlank(hm),
      relationship: isBlank(rel),
    };
  };

  const anyBeneficiaryPendingRow = benListForPending.some((b, idx) =>
    Object.values(beneficiaryFieldMissing(b, idx)).some(Boolean),
  );
  const anyTrustedContactPendingRow = tcListForPending.some((tc, idx) =>
    Object.values(trustedContactFieldMissing(tc, idx)).some(Boolean),
  );

  const lplInvalid =
    selectedField === "transfer_info" &&
    !validateLplAccountNumber(
      getFormValue(
        "old_account_number",
        account.transfer_info?.old_account_number || "",
      ),
      getFormValue(
        "delivering_firm_name",
        account.transfer_info?.delivering_firm_name || "",
      ),
    ).valid;

  const isBankSection =
    selectedField === "bank_details" || selectedField === "bank_accounts";
  const newBankRoutingLive = getFormValue("newBank.routing_number", "");
  const routingInvalid =
    isBankSection &&
    (incompleteBanksForRender.some(({ bank: b }) => {
      const rv = getFormValue(`banks.${b.id}.routing_number`, b.routing_number || "");
      return String(rv).trim() !== "" && !validateRoutingNumber(rv);
    }) ||
      (needsNewBank &&
        String(newBankRoutingLive).trim() !== "" &&
        !validateRoutingNumber(newBankRoutingLive)));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-start">
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-semibold text-gray-900 leading-snug">
              <span className="block">{getFieldTitle()}</span>
            </h3>
            {accountLabel && !isBankSection && (
              <p className="mt-1.5 text-sm text-gray-500 font-medium truncate">
                {accountLabel}
              </p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4">
          {saveError && (
            <div
              className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg"
              role="alert"
            >
              <p className="text-sm font-medium text-red-800 mb-1.5">
                Cannot save
              </p>
              <p className="text-sm text-red-700">{saveError}</p>
            </div>
          )}
          {/* Basic Details */}
          {selectedField === "basic_details" && (
            <div className="space-y-4">
              {showBasicCustodian && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custodian
                  </label>
                  <input
                    type="text"
                    value={getFormValue("custodian", account.custodian || "")}
                    onChange={(e) =>
                      handleFieldChange("custodian", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="Enter custodian"
                  />
                </div>
              )}
              {showBasicRegistration && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Registration
                  </label>
                  <select
                    value={getFormValue(
                      "registration",
                      account.registration || "",
                    )}
                    onChange={(e) => {
                      handleFieldChange("registration", e.target.value);
                      handleFieldChange("type", "");
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select...</option>
                    {REGISTRATION_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              )}
              {showBasicType && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                  </label>
                  <select
                    value={getFormValue("type", account.type || "")}
                    onChange={(e) =>
                      handleFieldChange("type", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select...</option>
                    {getAccountTypesForRegistration(getFormValue("registration", account.registration || "")).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              )}
              {showBasicProceeds && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Proceeds
                  </label>
                  <select
                    value={getFormValue("proceeds", account.proceeds || "")}
                    onChange={(e) =>
                      handleFieldChange("proceeds", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select...</option>
                    {PROCEEDS_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              )}
              {showBasicSecurityPurchases && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Security Purchases
                  </label>
                  <select
                    value={getFormValue("security_purchases", account.security_purchases || "")}
                    onChange={(e) =>
                      handleFieldChange("security_purchases", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select...</option>
                    {SECURITY_PURCHASES_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              )}
              {showBasicDistributionIncome && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dividend/Distribution Income
                  </label>
                  <select
                    value={getFormValue("distribution_income", account.distribution_income || DEFAULT_DIVIDEND_OPTION)}
                    onChange={(e) =>
                      handleFieldChange("distribution_income", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select...</option>
                    {DIVIDEND_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              )}
              {showBasicMargin && !isRetirementAccount(getFormValue("registration", account.registration || "")) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Are Margins enabled?
                  </label>
                  <select
                    value={
                      getFormValue("margin", "") !== ""
                        ? String(getFormValue("margin", ""))
                        : ""
                    }
                    onChange={(e) =>
                      handleFieldChange("margin", e.target.value === "true")
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select...</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              )}
              {showBasicOptions && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Are Options enabled?
                  </label>
                  <select
                    value={
                      getFormValue("options", "") !== ""
                        ? String(getFormValue("options", ""))
                        : ""
                    }
                    onChange={(e) =>
                      handleFieldChange("options", e.target.value === "true")
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select...</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              )}

              {showBasicAccountValue && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Value
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={getFormValue(
                      "account_value",
                      account.account_value ?? "",
                    )}
                    onChange={(e) =>
                      handleFieldChange("account_value", e.target.value)
                    }
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                      getFormValue("account_value", "") &&
                      isNaN(parseFloat(getFormValue("account_value", "")))
                        ? "bg-red-50/30 border-red-200"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter account value"
                  />
                </div>
              )}
              {!anyBasicFieldToShow && (
                <p className="text-sm text-gray-600">
                  No empty basic fields on file.
                </p>
              )}
            </div>
          )}

          {/* Risk Tolerance (Investment Strategy) */}
          {selectedField === "risk_tolerance" && (
            <div className="space-y-4">
              {showAccountRiskDetails(brokerageFlags, account) &&
                (!account.risk_tolerance?.invest_objectives ||
                account.risk_tolerance.invest_objectives.length === 0 ||
                account.risk_tolerance.invest_objectives.every(
                  (obj) => !obj,
                )) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Investment Objective{" "}
                  </label>
                  <select
                    value={getFormValue(
                      "invest_objectives",
                      account.risk_tolerance?.invest_objectives?.[0] || "",
                    )}
                    onChange={(e) =>
                      handleFieldChange("invest_objectives", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select Investment Objective</option>
                    {PRIMARY_OBJECTIVE_OPTIONS.map((obj) => (
                      <option key={obj} value={obj}>
                        {obj}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {showAccountRiskDetails(brokerageFlags, account) &&
                !account.risk_tolerance?.risk_tolerance && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Risk Tolerance
                  </label>
                  <select
                    value={getFormValue(
                      "risk_tolerance",
                      account.risk_tolerance?.risk_tolerance || "",
                    )}
                    onChange={(e) =>
                      handleFieldChange("risk_tolerance", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select Risk Tolerance</option>
                    {RISK_TOLERANCE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {showAccountRiskDetails(brokerageFlags, account) &&
                (!account.risk_tolerance?.time_horizon ||
                account.risk_tolerance.time_horizon.end === null ||
                account.risk_tolerance.time_horizon.end === undefined) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time Horizon
                  </label>
                  <select
                    value={formatAccountTimeHorizon(
                      getFormValue(
                        "time_horizon",
                        account.risk_tolerance?.time_horizon || "",
                      ),
                    )}
                    onChange={(e) =>
                      handleFieldChange("time_horizon", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select Time Horizon</option>
                    {ACCOUNT_TIME_HORIZON_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {showAccountDecisionMaking(brokerageFlags) &&
                !account.risk_tolerance?.decision_making && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Decision Making
                  </label>
                  <select
                    value={getFormValue(
                      "decision_making",
                      account.risk_tolerance?.decision_making || "",
                    )}
                    onChange={(e) =>
                      handleFieldChange("decision_making", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select Decision Making</option>
                    {DECISION_MAKING_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {showSourceOfFunds(brokerageFlags) &&
                (!account.risk_tolerance?.source_of_funds ||
                account.risk_tolerance.source_of_funds.length === 0 ||
                account.risk_tolerance.source_of_funds.every((s) => !s)) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Source of Funds
                  </label>
                  <select
                    value={getFormValue(
                      "source_of_funds",
                      account.risk_tolerance?.source_of_funds?.[0] || "",
                    )}
                    onChange={(e) =>
                      handleFieldChange("source_of_funds", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select Source of Funds</option>
                    {SOURCE_OF_FUNDS_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {showUseOfFunds(brokerageFlags) && !account.risk_tolerance?.use_of_funds && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Purpose of Account
                  </label>
                  <select
                    value={getFormValue(
                      "use_of_funds",
                      account.risk_tolerance?.use_of_funds || "",
                    )}
                    onChange={(e) =>
                      handleFieldChange("use_of_funds", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select Use of Funds</option>
                    {PRIMARY_USE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {(() => {
                if (!showLiquidityRequirements(brokerageFlags)) return null;
                const amountRange =
                  account.risk_tolerance?.liquidity_requirements?.amount;
                const isMissing =
                  amountRange?.start === undefined ||
                  amountRange?.start === null ||
                  amountRange?.end === undefined ||
                  amountRange?.end === null;
                if (!isMissing) return null;
                return (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Liquidity Needs ($)
                    </label>
                    <select
                      value={(() => {
                        const raw = getFormValue(
                          "liquidity_requirements.amount",
                          formatLiquidityNeeds(amountRange),
                        );
                        return typeof raw === "string"
                          ? raw
                          : formatLiquidityNeeds(raw);
                      })()}
                      onChange={(e) =>
                        handleFieldChange(
                          "liquidity_requirements.amount",
                          e.target.value,
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    >
                      <option value="">Select Liquidity Needs</option>
                      {LIQUIDITY_NEEDS_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })()}
              {(() => {
                if (!showLiquidityRequirements(brokerageFlags)) return null;
                const timeHorizonRange =
                  account.risk_tolerance?.liquidity_requirements?.time_horizon;
                const isMissing =
                  timeHorizonRange?.start === undefined ||
                  timeHorizonRange?.start === null ||
                  timeHorizonRange?.end === undefined ||
                  timeHorizonRange?.end === null;
                if (!isMissing) return null;
                return (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Liquidity Time Horizon (years)
                    </label>
                    <select
                      value={(() => {
                        const raw = getFormValue(
                          "liquidity_requirements.time_horizon",
                          formatLiquidityTimeHorizon(timeHorizonRange),
                        );
                        return typeof raw === "string"
                          ? raw
                          : formatLiquidityTimeHorizon(raw);
                      })()}
                      onChange={(e) =>
                        handleFieldChange(
                          "liquidity_requirements.time_horizon",
                          e.target.value,
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    >
                      <option value="">Select Liquidity Time Horizon</option>
                      {LIQUIDITY_TIME_HORIZON_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Transfer Info */}
          {selectedField === "transfer_info" && (
            <div className="space-y-4">
              {(!account.transfer_info?.old_account_number ||
                !validateLplAccountNumber(
                  account.transfer_info?.old_account_number,
                  account.transfer_info?.delivering_firm_name,
                ).valid) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Existing Account Number
                  </label>
                  <input
                    type="text"
                    value={getFormValue(
                      "old_account_number",
                      account.transfer_info?.old_account_number || "",
                    )}
                    onChange={(e) =>
                      handleFieldChange("old_account_number", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="Enter existing account number"
                  />
                  {(() => {
                    const lplError = validateLplAccountNumber(
                      getFormValue(
                        "old_account_number",
                        account.transfer_info?.old_account_number || "",
                      ),
                      getFormValue(
                        "delivering_firm_name",
                        account.transfer_info?.delivering_firm_name || "",
                      ),
                    ).error;
                    return lplError ? (
                      <p className="text-red-500 text-xs mt-1">{lplError}</p>
                    ) : null;
                  })()}
                </div>
              )}
              {!account.transfer_info?.delivering_firm_name && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Delivering Firm Name
                  </label>
                  <select
                    value={getFormValue(
                      "delivering_firm_name",
                      account.transfer_info?.delivering_firm_name || "",
                    )}
                    onChange={(e) =>
                      handleFieldChange("delivering_firm_name", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select delivering firm</option>
                    {deliveringFirmOptions(
                      getFormValue(
                        "delivering_firm_name",
                        account.transfer_info?.delivering_firm_name || "",
                      ),
                    ).map((firm) => (
                      <option key={firm} value={firm}>
                        {firm}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {(account.transfer_info?.is_full_transfer === null ||
                account.transfer_info?.is_full_transfer === undefined) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Is Full Transfer
                  </label>
                  <button
                    type="button"
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                      getFormValue(
                        "is_full_transfer",
                        account.transfer_info?.is_full_transfer !== false,
                      )
                        ? "bg-emerald-600"
                        : "bg-gray-200"
                    }`}
                    onClick={() => {
                      const currentValue = getFormValue(
                        "is_full_transfer",
                        account.transfer_info?.is_full_transfer !== false,
                      );
                      handleFieldChange("is_full_transfer", !currentValue);
                    }}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        getFormValue(
                          "is_full_transfer",
                          account.transfer_info?.is_full_transfer !== false,
                        )
                          ? "translate-x-6"
                          : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              )}
              {!account.transfer_info?.transfer_type && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transfer Type
                  </label>
                  <select
                    value={getFormValue(
                      "transfer_type",
                      account.transfer_info?.transfer_type ||
                        "Brokerage or Trust Company",
                    )}
                    onChange={(e) =>
                      handleFieldChange("transfer_type", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    {TYPE_OF_TRANSFER_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {(() => {
                const addressFields = [
                  { key: "address_line_1", label: "Address Line 1" },
                  { key: "address_line_2", label: "Address Line 2" },
                  { key: "city", label: "City" },
                  { key: "state", label: "State" },
                  { key: "postal_code", label: "Postal Code" },
                  { key: "country", label: "Country" },
                  { key: "phone", label: "Phone" },
                ];

                const missingFields = addressFields.filter(
                  (f) =>
                    f.key !== "address_line_2" &&
                    !account.transfer_info?.address?.[f.key],
                );

                if (missingFields.length === 0) return null;

                const locationKeys = ["city", "state", "country"];
                const lineKeys = ["address_line_1", "address_line_2"];
                const lineFields = addressFields.filter((f) =>
                  lineKeys.includes(f.key),
                );
                const remainingTextFields = addressFields.filter(
                  (f) =>
                    !locationKeys.includes(f.key) && !lineKeys.includes(f.key),
                );

                const renderAddressInput = (field) => (
                  <div
                    key={field.key}
                    className={
                      field.key === "address_line_1" ||
                      field.key === "address_line_2"
                        ? "md:col-span-2"
                        : ""
                    }
                  >
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label}
                    </label>
                    <input
                      type="text"
                      value={getFormValue(
                        `address.${field.key}`,
                        account.transfer_info?.address?.[field.key] || "",
                      )}
                      onChange={(e) =>
                        handleFieldChange(
                          `address.${field.key}`,
                          e.target.value,
                        )
                      }
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                        (field.key === "phone" &&
                          getFormValue("address.phone", "") &&
                          !validatePhoneDigits(
                            getFormValue("address.phone", ""),
                          )) ||
                        (field.key === "postal_code" &&
                          getFormValue("address.postal_code", "") &&
                          !validateZipDigits(
                            getFormValue("address.postal_code", ""),
                          ))
                          ? "bg-red-50/30 border-red-200"
                          : "border-gray-300"
                      }`}
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                    />
                  </div>
                );

                return (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">
                      Address
                    </h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {lineFields.map(renderAddressInput)}
                      </div>
                      <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
                        <StateCitySelect
                          countryValue={getFormValue(
                            "address.country",
                            account.transfer_info?.address?.country || "USA",
                          )}
                          stateValue={getFormValue(
                            "address.state",
                            account.transfer_info?.address?.state || "",
                          )}
                          cityValue={getFormValue(
                            "address.city",
                            account.transfer_info?.address?.city || "",
                          )}
                          onCountryChange={(val) =>
                            handleFieldChange("address.country", val)
                          }
                          onStateChange={(val) =>
                            handleFieldChange("address.state", val)
                          }
                          onCityChange={(val) =>
                            handleFieldChange("address.city", val)
                          }
                          stateLabel="State"
                          cityLabel="City"
                          isEditing={true}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {remainingTextFields.map(renderAddressInput)}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Trust Info — only fields empty on the loaded account */}
          {selectedField === "trust_info" && (
            <div className="space-y-4">
              {showTrustField.trust_name && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trust Name
                  </label>
                  <input
                    type="text"
                    value={getFormValue(
                      "trust_name",
                      account.trust_info?.trust_name || "",
                    )}
                    onChange={(e) =>
                      handleFieldChange("trust_name", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="Enter trust name"
                  />
                </div>
              )}
              {showTrustField.trust_title && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trust Title (as per document)
                  </label>
                  <input
                    type="text"
                    value={getFormValue(
                      "trust_title",
                      account.trust_info?.trust_title || "",
                    )}
                    onChange={(e) =>
                      handleFieldChange("trust_title", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="Enter trust title"
                  />
                </div>
              )}
              {showTrustField.ssn_or_tin && !trustRevocableCurrent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tax ID Number
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={11}
                    value={getFormValue(
                      "ssn_or_tin",
                      trustRevocableCurrent
                        ? formatSSNInput(trustGrantorSsnRaw)
                        : account.trust_info?.ssn_or_tin
                          ? formatSSNInput(String(account.trust_info.ssn_or_tin))
                          : "",
                    )}
                    onChange={(e) => !trustRevocableCurrent && handleFieldChange("ssn_or_tin", e.target.value)}
                    disabled={trustRevocableCurrent}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm tracking-widest ${(() => {
                      if (trustRevocableCurrent) {
                        return trustGrantorSsnCount === 9
                          ? "border-gray-300 bg-gray-100 text-gray-500"
                          : "border-red-300 bg-red-50/30";
                      }
                      const v = getFormValue("ssn_or_tin", "");
                      return getTaxIdCountError(v)
                        ? "border-red-300 bg-red-50/30"
                        : "border-gray-300";
                    })()}`}
                    placeholder="XXX-XX-XXXX"
                  />
                  {(() => {
                    if (trustRevocableCurrent) {
                      if (trustGrantorSsnCount !== 9) {
                        return (
                          <p className="text-red-500 text-xs mt-1">
                            Selected grantor does not have a valid 9-digit SSN/TIN.
                          </p>
                        );
                      }
                      return (
                        <p className="text-xs text-gray-500 mt-1">
                          For revocable trusts, SSN/TIN uses the grantor&apos;s SSN/TIN.
                        </p>
                      );
                    }
                    const tinError = getTaxIdCountError(getFormValue("ssn_or_tin", ""));
                    if (tinError) {
                      return (
                        <p className="text-red-500 text-xs mt-1">
                          {tinError}
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
              {showTrustField.trust_state && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <select
                    value={getFormValue(
                      "trust_state",
                      account.trust_info?.trust_state || "",
                    )}
                    onChange={(e) =>
                      handleFieldChange("trust_state", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select State</option>
                    {US_STATES.map((s) => (
                      <option key={s.abbreviation} value={s.abbreviation}>
                        {s.name} ({s.abbreviation})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {showTrustField.execution_date && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Execution Date
                  </label>
                  <DateInput
                    value={getFormValue(
                      "execution_date",
                      account.trust_info?.execution_date
                        ? formatDateForInput(account.trust_info.execution_date)
                        : "",
                    )}
                    onChange={(val) => handleFieldChange("execution_date", val)}
                    placeholder="mm-dd-yyyy"
                    minDate={pastDatePickerBounds.minDate}
                    maxDate={pastDatePickerBounds.maxDate}
                  />
                </div>
              )}
              {showTrustField.grantorIds && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Grantor/Settler Name
                  </label>
                  {(() => {
                    const formGrantorIds =
                      formData.grantorIds !== undefined
                        ? formData.grantorIds
                        : getTrustGrantorIds(account.trust_info);
                    const rows =
                      formGrantorIds.length > 0 ? formGrantorIds : [""];
                    const setGrantorIds = (next) =>
                      handleFieldChange("grantorIds", next);
                    const memberOpts = (members || []).map((m) => ({
                      id: m.id,
                      label: m.name || "",
                    }));
                    return (
                      <div className="space-y-2">
                        {rows.map((gid, idx) => (
                          <div key={`g-${idx}`} className="flex gap-2">
                            <select
                              value={gid || ""}
                              onChange={(e) => {
                                const next = [...rows];
                                next[idx] = e.target.value;
                                setGrantorIds(next);
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            >
                              <option value="">Select member...</option>
                              {memberOpts
                                .filter(
                                  (m) =>
                                    !rows.some(
                                      (r, i) =>
                                        i !== idx &&
                                        String(r || "") === String(m.id),
                                    ),
                                )
                                .map((m) => (
                                  <option key={m.id} value={m.id}>
                                    {m.label}
                                  </option>
                                ))}
                            </select>
                            {rows.length > 1 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setGrantorIds(
                                    rows.filter((_, i) => i !== idx),
                                  )
                                }
                                className="px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                        {memberOpts.filter(
                          (m) =>
                            !rows.some(
                              (r) => String(r || "") === String(m.id),
                            ),
                        ).length > 0 && (
                          <button
                            type="button"
                            onClick={() => setGrantorIds([...rows, ""])}
                            className="px-3 py-2 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm"
                          >
                            + Add Grantor
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
              {showTrustField.trustee_members && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trustee Names
                  </label>
                  <div className="space-y-3">
                    {(getFormValue("trustees", formData.trustees || []) || []).map(
                      (trustee, idx) => {
                        const currentGrantorIds =
                          formData.grantorIds !== undefined
                            ? formData.grantorIds
                            : getTrustGrantorIds(account.trust_info);
                        const primaryGrantorSel = currentGrantorIds[0] || "";
                        const rows =
                          getFormValue("trustees", formData.trustees || []) || [];
                        const tid = trustee?.id || "";
                        const showRelationship =
                          !tid ||
                          !primaryGrantorSel ||
                          String(tid) !== String(primaryGrantorSel);
                        const takenIds = new Set(
                          rows
                            .map((r, j) =>
                              j !== idx && r?.id ? String(r.id) : null,
                            )
                            .filter(Boolean),
                        );
                        const memberChoices = members.filter(
                          (m) =>
                            !takenIds.has(String(m.id)) ||
                            String(m.id) === String(tid),
                        );
                        return (
                        <div
                          key={idx}
                          className={`grid grid-cols-1 gap-3 items-end ${showRelationship ? "md:grid-cols-2" : ""}`}
                        >
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Trustee {idx + 1}
                            </label>
                            <select
                              value={tid}
                              onChange={(e) => {
                                const v = e.target.value;
                                handleFieldChange(`trustees.${idx}.id`, v);
                                const g = primaryGrantorSel;
                                if (g && v && String(v) === String(g)) {
                                  handleFieldChange(
                                    `trustees.${idx}.relationship_to_grantor`,
                                    null,
                                  );
                                }
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                            >
                              <option value="">Select Member</option>
                              {memberChoices.map((member) => (
                                <option key={member.id} value={member.id}>
                                  {member.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          {showRelationship ? (
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Relationship to Grantor
                              </label>
                              <div className="flex gap-2">
                                <select
                                  value={trustee?.relationship_to_grantor || ""}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      `trustees.${idx}.relationship_to_grantor`,
                                      e.target.value,
                                    )
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                >
                                  <option value="">Select relationship...</option>
                                  {RELATIONSHIP_OPTIONS.map((rel) => (
                                    <option key={rel} value={rel}>
                                      {rel}
                                    </option>
                                  ))}
                                </select>
                                {rows.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const next = rows.filter((_, i) => i !== idx);
                                      handleFieldChange("trustees", next);
                                    }}
                                    className="px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm shrink-0"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            </div>
                          ) : (
                            rows.length > 1 && (
                              <div className="flex md:justify-end">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const next = rows.filter((_, i) => i !== idx);
                                    handleFieldChange("trustees", next);
                                  }}
                                  className="px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm"
                                >
                                  Remove
                                </button>
                              </div>
                            )
                          )}
                        </div>
                        );
                      },
                    )}
                    {(() => {
                      const rowsForAdd =
                        getFormValue("trustees", formData.trustees || []) || [];
                      const selectedIds = new Set(
                        rowsForAdd
                          .map((r) => getTrusteeMemberId(r))
                          .filter(Boolean)
                          .map(String),
                      );
                      const canAddMoreTrustees = members.some(
                        (m) => !selectedIds.has(String(m.id)),
                      );
                      return (
                        <button
                          type="button"
                          disabled={!canAddMoreTrustees}
                          title={
                            canAddMoreTrustees
                              ? ""
                              : "Every household member is already assigned as a trustee."
                          }
                          onClick={() => {
                            const rows =
                              getFormValue("trustees", formData.trustees || []) ||
                              [];
                            handleFieldChange("trustees", [
                              ...rows,
                              { id: "", relationship_to_grantor: "" },
                            ]);
                          }}
                          className="px-3 py-2 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                          + Add Trustee
                        </button>
                      );
                    })()}
                  </div>
                </div>
              )}
              {showTrustField.trustee_relationships &&
                (() => {
                  const trusteesList = getFormValue(
                    "trustees",
                    normalizeTrusteesForForm(
                      account.trust_info?.trustees || [],
                    ),
                  );
                  return (
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-gray-700">
                        Relationship to Grantor
                      </p>
                      {trusteesList.map((tr, idx) => {
                        const memberId = getTrusteeMemberId(tr);
                        const gId =
                          (formData.grantorIds !== undefined
                            ? formData.grantorIds
                            : getTrustGrantorIds(account.trust_info))[0] || "";
                        if (
                          !memberId ||
                          (gId && String(memberId) === String(gId)) ||
                          !isPlaceholderTrusteeRelationship(
                            getTrusteeRelationshipToGrantor(tr),
                          )
                        ) {
                          return null;
                        }
                        const displayName =
                          members.find(
                            (m) => String(m.id) === String(memberId),
                          )?.name || `Trustee ${idx + 1}`;
                        const currentRel = getFormValue(
                          `trustees.${idx}.relationship_to_grantor`,
                          getTrusteeRelationshipToGrantor(tr),
                        );
                        const selectValue =
                          isPlaceholderTrusteeRelationship(currentRel)
                            ? ""
                            : currentRel || "";
                        return (
                          <div key={`${memberId}-${idx}`}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {displayName}
                            </label>
                            <select
                              value={selectValue}
                              onChange={(e) =>
                                handleFieldChange(
                                  `trustees.${idx}.relationship_to_grantor`,
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                            >
                              <option value="">
                                Select
                              </option>
                              {RELATIONSHIP_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              {!anyTrustFieldToShow && (
                <p className="text-sm text-gray-600">
                  No empty trust fields on file.
                </p>
              )}
            </div>
          )}

          {selectedField === "business_info" && (
            <div className="space-y-4">
              {showBizField.business_name && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Name
                  </label>
                  <input
                    type="text"
                    value={getFormValue("business_name", "")}
                    onChange={(e) =>
                      handleFieldChange("business_name", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="Enter business name"
                  />
                </div>
              )}
              {showBizField.ssn && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Tax ID (EIN)
                  </label>
                  <input
                    type="text"
                    value={formatSSNInput(getFormValue("ssn", ""))}
                    onChange={(e) =>
                      handleFieldChange(
                        "ssn",
                        e.target.value.replace(/\D/g, "").substring(0, 9),
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="XX-XXXXXXX"
                  />
                </div>
              )}
              {showBizField.is_operating && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Is the business operating?
                  </label>
                  <select
                    value={getFormValue("is_operating", "")}
                    onChange={(e) =>
                      handleFieldChange("is_operating", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select...</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              )}
              {showBizField.is_operating && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Non-Profit
                  </label>
                  <input
                    type="checkbox"
                    checked={
                      getFormValue("is_non_profit", bizSnap.is_non_profit === true) === true
                    }
                    onChange={(e) =>
                      handleFieldChange("is_non_profit", e.target.checked)
                    }
                    className="w-4 h-4 text-emerald-500 border-gray-300 rounded focus:ring-emerald-500 cursor-pointer"
                  />
                </div>
              )}
              {showBizField.tax_classification && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tax Classification
                  </label>
                  <select
                    value={getFormValue("tax_classification", "")}
                    onChange={(e) =>
                      handleFieldChange("tax_classification", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select...</option>
                    {bizTaxOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {showBizField.control_persons && (
                <div>
                  <MemberSelect
                    multi
                    label="Control Persons"
                    required
                    members={allHouseholdMembers}
                    value={getFormValue("control_persons", [])}
                    onChange={(next) =>
                      handleFieldChange("control_persons", next)
                    }
                    addLabel="+ Add Control Person"
                    maxEntries={4}
                  />
                </div>
              )}
              {showBizField.address && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address Line 1
                    </label>
                    <input
                      type="text"
                      value={getFormValue(
                        "address.address_line_1",
                        bizSnap.address?.address_line_1 || "",
                      )}
                      onChange={(e) =>
                        handleFieldChange("address.address_line_1", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                      placeholder="Enter address line 1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      value={getFormValue(
                        "address.address_line_2",
                        bizSnap.address?.address_line_2 || "",
                      )}
                      onChange={(e) =>
                        handleFieldChange("address.address_line_2", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                      placeholder="Enter address line 2 (optional)"
                    />
                  </div>
                  <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
                    <StateCitySelect
                      countryValue={getFormValue(
                        "address.country",
                        bizSnap.address?.country || "USA",
                      )}
                      stateValue={getFormValue(
                        "address.province",
                        bizSnap.address?.province || "",
                      )}
                      cityValue={getFormValue(
                        "address.city",
                        bizSnap.address?.city || "",
                      )}
                      onCountryChange={(val) => {
                        handleFieldChange("address.country", val);
                        handleFieldChange("address.province", "");
                        handleFieldChange("address.city", "");
                      }}
                      onStateChange={(val) =>
                        handleFieldChange("address.province", val)
                      }
                      onCityChange={(val) =>
                        handleFieldChange("address.city", val)
                      }
                      isEditing={true}
                      stateLabel="State"
                      cityLabel="City"
                      zipValue={getFormValue(
                        "address.postal_code",
                        bizSnap.address?.postal_code || "",
                      )}
                      onZipChange={(val) =>
                        handleFieldChange("address.postal_code", val)
                      }
                      zipLabel="Postal Code"
                      zipRestrictUsFive
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="text"
                      value={getFormValue(
                        "address.phone",
                        bizSnap.address?.phone
                          ? formatPhoneInput(String(bizSnap.address.phone))
                          : "",
                      )}
                      onChange={(e) =>
                        handleFieldChange("address.phone", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                      placeholder="XXX-XXX-XXXX"
                    />
                  </div>
                </div>
              )}
              {!anyBizFieldToShow && (
                <p className="text-sm text-gray-600">
                  No empty business fields on file.
                </p>
              )}
            </div>
          )}

          {selectedField === "inheritance_info" && (
            <div className="space-y-4">
              {showInheritanceField.decedent && (
                <div>
                  <MemberSelect
                    label="Decedent"
                    required
                    members={allHouseholdMembers}
                    value={getFormValue("decedent_id", "")}
                    onChange={(id) => handleFieldChange("decedent_id", id)}
                  />
                </div>
              )}
              {showInheritanceField.relationship && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Owner's Relationship to Decedent
                  </label>
                  <select
                    value={getFormValue("relationship", "")}
                    onChange={(e) =>
                      handleFieldChange("relationship", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select...</option>
                    {RELATIONSHIP_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {!anyInheritanceFieldToShow && (
                <p className="text-sm text-gray-600">
                  No empty inheritance fields on file.
                </p>
              )}
            </div>
          )}

          {selectedField === "minor_details" && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                A UTMA/UGMA account requires the minor as the second account owner.
              </p>
              <MemberSelect
                label="Minor"
                required
                members={allHouseholdMembers.filter(
                  (m) =>
                    !(Array.isArray(account.owners_info) ? account.owners_info : []).some(
                      (o) => o && String(o.id) === String(m.id),
                    ),
                )}
                value={getFormValue("minor_member_id", "")}
                onChange={(id) => handleFieldChange("minor_member_id", id)}
              />
              <p className="text-xs text-gray-500">
                If the minor isn't listed, add them from the Household Members tab first.
              </p>
            </div>
          )}

          {(selectedField === "bank_details" ||
            selectedField === "bank_accounts") && (
            <div className="space-y-6">
              {hasBankDocMissing && custodian === "Fidelity" && (
                <div className="space-y-2">
                  <div className="flex items-center gap-3 border border-amber-200 bg-amber-50 rounded-lg p-3">
                    <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-sm font-medium text-amber-900 flex-1">
                      Please upload a Voided Check or Bank Statement for this Bank Account.
                    </p>
                    <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors shrink-0 ${bankDocUploading ? "bg-amber-200 text-amber-500 cursor-not-allowed" : "bg-amber-600 text-white hover:bg-amber-700 cursor-pointer"}`}>
                      Choose File
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        disabled={bankDocUploading}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          e.target.value = "";
                          if (file) {
                            setBankDocSelectedFile(file);
                            setBankDocError("");
                            setBankDocUploaded(false);
                          }
                        }}
                      />
                    </label>
                  </div>
                  {bankDocSelectedFile && (
                    <div className="flex items-center gap-3 border border-gray-200 bg-white rounded-lg p-3">
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 truncate">{bankDocSelectedFile.name}</p>
                        <p className="text-xs text-gray-400">
                          {bankDocUploading ? "Uploading…" : "Will be uploaded when you click Save & Next."}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setBankDocSelectedFile(null)}
                        disabled={bankDocUploading}
                        aria-label="Remove selected file"
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  {bankDocUploaded && !bankDocError && (
                    <p className="text-xs text-emerald-700">
                      Uploaded{bankDocFileName ? `: ${bankDocFileName}` : ""}. It will be processed and linked to this bank account automatically.
                    </p>
                  )}
                  {bankDocError && (
                    <p className="text-xs text-red-600">{bankDocError}</p>
                  )}
                </div>
              )}
              {incompleteBanksForRender.map(({ bank }) => {
                const baId = bank.id;
                const bankNameVal = getFormValue(
                  `banks.${baId}.bank_name`,
                  bank.bank_name || "",
                );
                const accountNameVal = getFormValue(
                  `banks.${baId}.account_name`,
                  bank.account_name || "",
                );
                const accountNumberVal = getFormValue(
                  `banks.${baId}.account_number`,
                  bank.account_number || "",
                );
                const routingVal = getFormValue(
                  `banks.${baId}.routing_number`,
                  bank.routing_number || "",
                );
                const routingInvalidLive =
                  String(routingVal).trim() !== "" &&
                  !validateRoutingNumber(routingVal);
                const accountTypeVal = getFormValue(
                  `banks.${baId}.account_type`,
                  canonicalBankAccountType(bank.account_type),
                );
                const isPersonalVal = getFormValue(
                  `banks.${baId}.is_personal`,
                  bank.is_personal ?? true,
                );
                const req = <span className="text-red-500">*</span>;
                return (
                  <div
                    key={baId}
                    className="border border-gray-200 rounded-lg p-4 bg-white"
                  >
                    <div className="text-sm font-semibold text-gray-900 mb-3">
                      {formatBankLabel(bank)}
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Bank Name {req}
                        </label>
                        <input
                          type="text"
                          value={bankNameVal}
                          onChange={(e) =>
                            handleFieldChange(`banks.${baId}.bank_name`, e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          placeholder="e.g., Chase Bank"
                          maxLength={100}
                          disabled={saving}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Account Name {req}
                        </label>
                        <input
                          type="text"
                          value={accountNameVal}
                          onChange={(e) =>
                            handleFieldChange(`banks.${baId}.account_name`, e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          placeholder="e.g., John & Jane Doe Joint Account"
                          maxLength={100}
                          disabled={saving}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Account Number {req}
                        </label>
                        <input
                          type="text"
                          value={accountNumberVal}
                          onChange={(e) =>
                            handleFieldChange(`banks.${baId}.account_number`, e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          placeholder="e.g., 1234567890"
                          maxLength={30}
                          disabled={saving}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Routing Number {req}
                        </label>
                        <input
                          type="text"
                          value={routingVal}
                          onChange={(e) =>
                            handleFieldChange(`banks.${baId}.routing_number`, e.target.value)
                          }
                          className={`w-full px-3 py-2 border ${
                            routingInvalidLive ? "border-red-500" : "border-gray-300"
                          } rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm`}
                          placeholder="e.g., 021000021"
                          maxLength={9}
                          disabled={saving}
                        />
                        {routingInvalidLive && (
                          <p className="text-red-500 text-xs mt-1">
                            Enter a valid 9-digit routing number
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Account Type {req}
                        </label>
                        <select
                          value={accountTypeVal}
                          onChange={(e) =>
                            handleFieldChange(`banks.${baId}.account_type`, e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          disabled={saving}
                        >
                          <option value="">Select Account Type</option>
                          {BANK_ACCOUNT_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.label}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Account Category {req}
                        </label>
                        <div className="flex bg-gray-100 rounded-lg p-1 w-full md:w-64">
                          <button
                            type="button"
                            onClick={() =>
                              handleFieldChange(`banks.${baId}.is_personal`, true)
                            }
                            className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                              isPersonalVal
                                ? "bg-white text-emerald-700 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                            disabled={saving}
                          >
                            Personal
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleFieldChange(`banks.${baId}.is_personal`, false)
                            }
                            className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                              !isPersonalVal
                                ? "bg-white text-emerald-700 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                            disabled={saving}
                          >
                            Business
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {needsNewBank && (
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="text-sm font-semibold text-gray-900 mb-1">
                    No bank account linked
                  </div>
                  <p className="text-xs text-gray-500 mb-4">
                    This investment account has a withdrawal or deposit but no
                    linked bank account. Add or link one below.
                  </p>

                  {availableBanksForLink.length > 0 && (
                    <div className="flex bg-gray-100 rounded-lg p-1 w-full md:w-96 mb-5">
                      <button
                        type="button"
                        onClick={() =>
                          handleFieldChange("bankAddMode", "link")
                        }
                        className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                          getFormValue("bankAddMode", "link") === "link"
                            ? "bg-white text-emerald-700 shadow-sm"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                        disabled={saving}
                      >
                        Link existing
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleFieldChange("bankAddMode", "create")
                        }
                        className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                          getFormValue("bankAddMode", "link") === "create"
                            ? "bg-white text-emerald-700 shadow-sm"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                        disabled={saving}
                      >
                        Add new
                      </button>
                    </div>
                  )}

                  {availableBanksForLink.length > 0 &&
                  getFormValue("bankAddMode", "link") === "link" ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select a bank account from this household
                      </label>
                      <select
                        value={getFormValue(
                          "linkBankId",
                          availableBanksForLink[0]?.id || "",
                        )}
                        onChange={(e) =>
                          handleFieldChange("linkBankId", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        disabled={saving}
                      >
                        {availableBanksForLink.map((ba) => (
                          <option key={ba.id} value={ba.id}>
                            {formatBankLabel(ba)}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-2">
                        This bank account will be linked to the investment
                        account. No fields will be changed.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Bank Name
                        </label>
                        <input
                          type="text"
                          value={getFormValue("newBank.bank_name", "")}
                          onChange={(e) =>
                            handleFieldChange(
                              "newBank.bank_name",
                              e.target.value,
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          placeholder="Enter bank name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Account Name
                        </label>
                        <input
                          type="text"
                          value={getFormValue("newBank.account_name", "")}
                          onChange={(e) =>
                            handleFieldChange(
                              "newBank.account_name",
                              e.target.value,
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          placeholder="Enter account name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Account Number
                        </label>
                        <input
                          type="text"
                          value={getFormValue("newBank.account_number", "")}
                          onChange={(e) =>
                            handleFieldChange(
                              "newBank.account_number",
                              e.target.value,
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          placeholder="Enter account number"
                        />
                      </div>
                      {(() => {
                        const newRoutingLive = getFormValue(
                          "newBank.routing_number",
                          "",
                        );
                        const newRoutingLiveInvalid =
                          String(newRoutingLive).trim() !== "" &&
                          !validateRoutingNumber(newRoutingLive);
                        return (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Routing Number
                            </label>
                            <input
                              type="text"
                              value={newRoutingLive}
                              onChange={(e) =>
                                handleFieldChange(
                                  "newBank.routing_number",
                                  e.target.value,
                                )
                              }
                              className={`w-full px-3 py-2 border ${
                                newRoutingLiveInvalid
                                  ? "border-red-500"
                                  : "border-gray-300"
                              } rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm`}
                              placeholder="Enter routing number (9 digits)"
                              maxLength={9}
                            />
                            {newRoutingLiveInvalid && (
                              <p className="text-red-500 text-xs mt-1">
                                Enter a valid 9-digit routing number
                              </p>
                            )}
                          </div>
                        );
                      })()}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Account Type
                        </label>
                        <select
                          value={getFormValue("newBank.account_type", "")}
                          onChange={(e) =>
                            handleFieldChange(
                              "newBank.account_type",
                              e.target.value,
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        >
                          <option value="">Select Account Type</option>
                          {BANK_ACCOUNT_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.label}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Account Category
                        </label>
                        <div className="flex bg-gray-100 rounded-lg p-1 w-full md:w-64">
                          <button
                            type="button"
                            onClick={() =>
                              handleFieldChange("newBank.is_personal", true)
                            }
                            className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                              getFormValue("newBank.is_personal", true)
                                ? "bg-white text-emerald-700 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                            disabled={saving}
                          >
                            Personal
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleFieldChange(
                                "newBank.is_personal",
                                false,
                              )
                            }
                            className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                              !getFormValue("newBank.is_personal", true)
                                ? "bg-white text-emerald-700 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                            disabled={saving}
                          >
                            Business
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {!needsNewBank &&
                !anyBankFieldToShow &&
                !(hasBankDocMissing && custodian === "Fidelity") && (
                  <p className="text-sm text-gray-600">
                    All bank account details for this account are complete. If
                    you just uploaded a document, it will be processed and
                    linked automatically — save to refresh.
                  </p>
                )}
            </div>
          )}

          {/* Withdrawals */}
          {selectedField === "withdrawals" && (
            <div className="space-y-6">
              {(formData.withdrawals || [])
                .map((w, originalIndex) => ({ ...w, originalIndex }))
                .map((withdrawal) => {
                  const index = withdrawal.originalIndex;
                  const federalVal = getFormValue(
                    `withdrawals.${index}.federal_tax_withholding`,
                    "",
                  );
                  const stateVal = getFormValue(
                    `withdrawals.${index}.state_tax_withholding`,
                    0,
                  );
                  const atLeastOneTaxSet =
                    (federalVal !== "" &&
                      federalVal !== null &&
                      !isNaN(parseFloat(federalVal))) ||
                    (stateVal !== "" &&
                      stateVal !== null &&
                      !isNaN(parseFloat(stateVal)));
                  const taxError = !atLeastOneTaxSet;
                  const destVal = getFormValue(
                    `withdrawals.${index}.destination`,
                    "",
                  );
                  const destIdVal = getFormValue(
                    `withdrawals.${index}.destination_identifier`,
                    "",
                  );
                  return (
                    <div
                      key={withdrawal.id || index}
                      className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex justify-between items-center">
                        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                          <span className="bg-emerald-100 text-emerald-700 w-6 h-6 rounded-full flex items-center justify-center text-xs">
                            {index + 1}
                          </span>
                          Withdrawal Details
                        </h3>
                        <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 text-red-600 text-[10px] font-bold uppercase tracking-wider border border-red-100 animate-pulse">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                          Details Required
                        </span>
                      </div>

                      <div className="p-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                              Federal Tax Withholding (%){" "}
                            </label>
                            <input
                              type="number"
                              min={0}
                              max={100}
                              value={getFormValue(
                                `withdrawals.${index}.federal_tax_withholding`,
                                "",
                              )}
                              onChange={(e) =>
                                handleFieldChange(
                                  `withdrawals.${index}.federal_tax_withholding`,
                                  e.target.value,
                                )
                              }
                              className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all ${taxError ? "bg-red-50/30 border-red-200" : "bg-gray-50 border-gray-300"}`}
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                              State Tax Withholding (%){" "}
                            </label>
                            <input
                              type="number"
                              min={0}
                              max={100}
                              value={getFormValue(
                                `withdrawals.${index}.state_tax_withholding`,
                                0,
                              )}
                              onChange={(e) =>
                                handleFieldChange(
                                  `withdrawals.${index}.state_tax_withholding`,
                                  e.target.value,
                                )
                              }
                              className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all ${taxError ? "bg-red-50/30 border-red-200" : "bg-gray-50 border-gray-300"}`}
                              placeholder="0"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                              Amount ($){" "}
                            </label>
                            <input
                              type="number"
                              min={0}
                              value={getFormValue(
                                `withdrawals.${index}.amount`,
                                "",
                              )}
                              onChange={(e) =>
                                handleFieldChange(
                                  `withdrawals.${index}.amount`,
                                  e.target.value,
                                )
                              }
                              className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all ${getFormValue(`withdrawals.${index}.amount`, "") === "" || getFormValue(`withdrawals.${index}.amount`, "") === null || (getFormValue(`withdrawals.${index}.amount`, "") && isNaN(parseFloat(getFormValue(`withdrawals.${index}.amount`, "")))) ? "bg-red-50/30 border-red-200" : "bg-gray-50 border-gray-300"}`}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                              Frequency{" "}
                            </label>
                            <select
                              value={getFormValue(
                                `withdrawals.${index}.frequency`,
                                "",
                              )}
                              onChange={(e) =>
                                handleFieldChange(
                                  `withdrawals.${index}.frequency`,
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all"
                            >
                              <option value="">Select Frequency</option>
                              {frequencyOptions.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                              Start Date{" "}
                            </label>
                            <DateInput
                              value={
                                getFormValue(
                                  `withdrawals.${index}.start_date`,
                                  "",
                                )
                                  ? formatDateForInput(
                                      getFormValue(
                                        `withdrawals.${index}.start_date`,
                                        "",
                                      ),
                                    )
                                  : ""
                              }
                              onChange={(val) =>
                                handleFieldChange(
                                  `withdrawals.${index}.start_date`,
                                  val,
                                )
                              }
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all"
                              placeholder="mm-dd-yyyy"
                              minDate={scheduleDatePickerBounds.minDate}
                              maxDate={scheduleDatePickerBounds.maxDate}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                              Type{" "}
                            </label>
                            <select
                              value={getFormValue(
                                `withdrawals.${index}.type`,
                                withdrawal.type || "",
                              )}
                              onChange={(e) =>
                                handleFieldChange(
                                  `withdrawals.${index}.type`,
                                  e.target.value,
                                )
                              }
                              className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all ${getFormValue(`withdrawals.${index}.type`, "") === "" || getFormValue(`withdrawals.${index}.type`, "") === null ? "bg-red-50/30 border-red-200" : "bg-gray-50 border-gray-300"}`}
                            >
                              <option value="">Select type</option>
                              <option value="Fixed Amount">Fixed Amount</option>
                              <option value="RMD">RMD</option>
                              <option value="Interest">Interest</option>
                              <option value="Income Only">Income Only</option>
                            </select>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                              Method{" "}
                            </label>
                            <select
                              value={getFormValue(
                                `withdrawals.${index}.method`,
                                "",
                              )}
                              onChange={(e) =>
                                handleFieldChange(
                                  `withdrawals.${index}.method`,
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all"
                            >
                              <option value="">Select Method</option>
                              {WITHDRAWAL_METHOD_OPTIONS.map((o) => (
                                <option key={o} value={o}>
                                  {o}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                              Destination{" "}
                            </label>
                            <select
                              value={getFormValue(
                                `withdrawals.${index}.destination`,
                                "",
                              )}
                              onChange={(e) => {
                                handleFieldChange(
                                  `withdrawals.${index}.destination`,
                                  e.target.value,
                                );
                                handleFieldChange(
                                  `withdrawals.${index}.destination_identifier`,
                                  "",
                                );
                              }}
                              className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all ${!destVal ? "bg-red-50/30 border-red-200" : "bg-gray-50 border-gray-300"}`}
                            >
                              <option value="">Select Destination</option>
                              {destinationOptions.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {destVal && (
                          <div className="animate-in fade-in slide-in-from-top-1 duration-200">
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                              Destination Identifier
                            </label>
                            <select
                              value={destIdVal}
                              onChange={(e) =>
                                handleFieldChange(
                                  `withdrawals.${index}.destination_identifier`,
                                  e.target.value,
                                )
                              }
                              className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all ${!destIdVal ? "bg-red-50/30 border-red-200" : "bg-gray-50 border-gray-300"}`}
                            >
                              <option value="">Select {destVal}</option>
                              {destVal === "Brokerage Account" &&
                                getAllAccounts().map((acc, idx) => {
                                  const actualIndex =
                                    household?.accounts?.findIndex(
                                      (a) => a.id === acc.id,
                                    );
                                  return (
                                    <option key={acc.id} value={acc.id}>
                                      {formatAccountName(
                                        acc,
                                        actualIndex + 1,
                                        household?.members || [],
                                      )}
                                    </option>
                                  );
                                })}
                              {getFormValue(
                                `withdrawals.${index}.destination`,
                                "",
                              ) === "Bank Account" &&
                                getAllBankAccounts().map((bank, idx) => (
                                  <option
                                    key={bank.id || idx}
                                    value={bank.id || idx}
                                  >
                                    {bank.bank_name || "Bank"} -{" "}
                                    {bank.account_number || "N/A"}
                                  </option>
                                ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

              {(!formData.withdrawals || formData.withdrawals.length === 0) && (
                <div className="text-center py-12 px-4 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No withdrawals found
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    There are no withdrawals scheduled for this account.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Deposits */}
          {selectedField === "deposits" && (
            <div className="space-y-4">
              {showDepositField.amount && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount ($){" "}
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={getFormValue("amount", depositSnap?.amount ?? "")}
                    onChange={(e) =>
                      handleFieldChange("amount", e.target.value)
                    }
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                      getFormValue("amount", "") === "" ||
                      (getFormValue("amount", "") &&
                        isNaN(parseFloat(getFormValue("amount", ""))))
                        ? "bg-red-50/30 border-red-200"
                        : "border-gray-300"
                    }`}
                    placeholder="0"
                  />
                </div>
              )}
              {showDepositField.frequency && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Frequency{" "}
                  </label>
                  <select
                    value={getFormValue(
                      "frequency",
                      depositSnap?.frequency || "",
                    )}
                    onChange={(e) =>
                      handleFieldChange("frequency", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select Frequency</option>
                    {frequencyOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {showDepositField.start_date && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date{" "}
                  </label>
                  <DateInput
                    value={getFormValue(
                      "start_date",
                      depositSnap?.start_date
                        ? formatDateForInput(depositSnap.start_date)
                        : "",
                    )}
                    onChange={(val) => handleFieldChange("start_date", val)}
                    placeholder="mm-dd-yyyy"
                    minDate={scheduleDatePickerBounds.minDate}
                    maxDate={scheduleDatePickerBounds.maxDate}
                  />
                </div>
              )}
              {showDepositField.type && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type{" "}
                  </label>
                  <input
                    type="text"
                    value={getFormValue("type", depositSnap?.type || "")}
                    onChange={(e) => handleFieldChange("type", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="Enter type"
                  />
                </div>
              )}
              {showDepositField.method && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Method{" "}
                  </label>
                  <select
                    value={getFormValue("method", depositSnap?.method || "")}
                    onChange={(e) =>
                      handleFieldChange("method", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  >
                    <option value="">Select Method</option>
                    {methodOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {!anyDepositFieldToShow && (
                <p className="text-sm text-gray-600">
                  No empty deposit fields on file.
                </p>
              )}
            </div>
          )}

          {selectedField === "beneficiaries" && (
            <div className="space-y-4">
              <p className="text-sm text-gray-700 leading-relaxed">
                Complete the missing beneficiary fields below. Only empty fields
                are shown for each row.
              </p>
              {benListForPending.length === 0 ? (
                <p className="text-sm text-gray-600">
                  No beneficiaries on this account. Add beneficiaries from the
                  Accounts tab first.
                </p>
              ) : !anyBeneficiaryPendingRow ? (
                <p className="text-sm text-gray-600">
                  No empty beneficiary fields on file. Save to refresh status
                  if data was updated elsewhere.
                </p>
              ) : (
                <div className="space-y-4">
                  {benListForPending.map((b, idx) => {
                    const missing = beneficiaryFieldMissing(b, idx);
                    if (!Object.values(missing).some(Boolean)) return null;
                    return (
                      <div
                        key={b?.id || `bene-${idx}`}
                        className="border border-amber-200 rounded-lg p-4 space-y-3 bg-amber-50/40"
                      >
                        <div className="text-sm font-semibold text-gray-900">
                          {getMemberDisplayName(b?.household_member_id)}
                        </div>
                        {missing.household_member_id && (
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                              Household member
                            </label>
                            <select
                              value={
                                getFormValue(
                                  `beneficiaries.${idx}.household_member_id`,
                                  b?.household_member_id,
                                ) || ""
                              }
                              onChange={(e) =>
                                handleFieldChange(
                                  `beneficiaries.${idx}.household_member_id`,
                                  e.target.value || "",
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                            >
                              <option value="">Select member</option>
                              {household?.members?.map((m) => {
                                if (ownerMemberIds.has(m.id)) return null;
                                const takenByOther = benListForPending.some(
                                  (row, j) =>
                                    j !== idx &&
                                    row?.household_member_id === m.id,
                                );
                                if (
                                  takenByOther &&
                                  m.id !== b?.household_member_id
                                ) {
                                  return null;
                                }
                                const fn = m.personal_info?.first_name || "";
                                const ln = m.personal_info?.last_name || "";
                                const label = `${fn} ${ln}`.trim() || "Member";
                                return (
                                  <option key={m.id} value={m.id}>
                                    {label}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        )}
                        {missing.type && (
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                              Type
                            </label>
                            <select
                              value={
                                getFormValue(
                                  `beneficiaries.${idx}.type`,
                                  b?.type,
                                ) || ""
                              }
                              onChange={(e) =>
                                handleFieldChange(
                                  `beneficiaries.${idx}.type`,
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                            >
                              <option value="">Select type</option>
                              <option value="primary">Primary</option>
                              <option value="contingent">Contingent</option>
                            </select>
                          </div>
                        )}
                        {missing.share && (
                          <div>
                            <div className="flex items-center gap-3 mb-1">
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Share %
                              </label>
                              <SharePresetButtons
                                value={
                                  parseFloat(
                                    getFormValue(
                                      `beneficiaries.${idx}.share`,
                                      b?.share,
                                    ),
                                  ) || 0
                                }
                                onChange={(val) =>
                                  handleFieldChange(
                                    `beneficiaries.${idx}.share`,
                                    val,
                                  )
                                }
                              />
                            </div>
                            <ShareSlider
                              value={
                                parseFloat(
                                  getFormValue(
                                    `beneficiaries.${idx}.share`,
                                    b?.share,
                                  ),
                                ) || 0
                              }
                              onChange={(val) =>
                                handleFieldChange(
                                  `beneficiaries.${idx}.share`,
                                  val,
                                )
                              }
                            />
                          </div>
                        )}
                        {missing.relationship && (
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                              Relationship to Account Owner
                            </label>
                            <select
                              value={
                                getFormValue(
                                  `beneficiaries.${idx}.relationship`,
                                  b?.relationship,
                                ) || ""
                              }
                              onChange={(e) =>
                                handleFieldChange(
                                  `beneficiaries.${idx}.relationship`,
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                            >
                              <option value="">
                                Select relationship to account owner
                              </option>
                              {RELATIONSHIP_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {selectedField === "trusted_contacts" && (
            <div className="space-y-4">
              <p className="text-sm text-gray-700 leading-relaxed">
                Complete the missing trusted contact fields below. Only empty
                fields are shown for each row.
              </p>
              {tcListForPending.length === 0 ? (
                <p className="text-sm text-gray-600">
                  No trusted contacts on this account. Add them from the
                  Accounts tab first.
                </p>
              ) : !anyTrustedContactPendingRow ? (
                <p className="text-sm text-gray-600">
                  No empty trusted contact fields on file. Save to refresh
                  status if data was updated elsewhere.
                </p>
              ) : (
                <div className="space-y-4">
                  {tcListForPending.map((tc, idx) => {
                    const missing = trustedContactFieldMissing(tc, idx);
                    if (!Object.values(missing).some(Boolean)) return null;
                    return (
                      <div
                        key={tc?.id || `tc-${idx}`}
                        className="border border-indigo-200 rounded-lg p-4 space-y-3 bg-indigo-50/40"
                      >
                        <div className="text-sm font-semibold text-gray-900">
                          {getMemberDisplayName(tc?.household_member_id)}
                        </div>
                        {missing.household_member_id && (
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                              Household member
                            </label>
                            <select
                              value={
                                getFormValue(
                                  `trusted_contacts.${idx}.household_member_id`,
                                  tc?.household_member_id,
                                ) || ""
                              }
                              onChange={(e) =>
                                handleFieldChange(
                                  `trusted_contacts.${idx}.household_member_id`,
                                  e.target.value || "",
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                            >
                              <option value="">Select member</option>
                              {household?.members?.map((m) => {
                                if (ownerMemberIds.has(m.id)) return null;
                                const fn = m.personal_info?.first_name || "";
                                const ln = m.personal_info?.last_name || "";
                                const label = `${fn} ${ln}`.trim() || "Member";
                                return (
                                  <option key={m.id} value={m.id}>
                                    {label}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        )}
                        {missing.relationship && (
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                              Relationship to Account Owner
                            </label>
                            <select
                              value={
                                getFormValue(
                                  `trusted_contacts.${idx}.relationship`,
                                  tc?.relationship,
                                ) || ""
                              }
                              onChange={(e) =>
                                handleFieldChange(
                                  `trusted_contacts.${idx}.relationship`,
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                            >
                              <option value="">
                                Select relationship to account owner
                              </option>
                              {RELATIONSHIP_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {(selectedField === "beneficiaries_primary_allocation" ||
            selectedField === "beneficiaries_contingent_allocation") && (
            <div className="space-y-4">
              <p className="text-sm text-gray-700 leading-relaxed">
                Adjust <strong>{allocationType}</strong> beneficiary share
                percentages so they <strong>total 100%</strong> for this
                account. You can save partial totals and finish later from the
                Beneficiaries section.
              </p>
              <div
                className={`rounded-lg border px-3 py-2 text-sm font-medium ${
                  isBeneficiaryShareTotalOver100(allocationRunningTotal)
                    ? "border-red-200 bg-red-50 text-red-800"
                    : Math.abs(allocationRunningTotal - 100) <
                        BENEFICIARY_TOTAL_EPS
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "border-amber-200 bg-amber-50 text-amber-900"
                }`}
              >
                Current {allocationType} total:{" "}
                {Math.round(allocationRunningTotal * 100) / 100}%
                {!isBeneficiaryShareTotalOver100(allocationRunningTotal) &&
                  Math.abs(allocationRunningTotal - 100) >=
                    BENEFICIARY_TOTAL_EPS && (
                    <span className="block mt-1 font-normal">
                      Remaining to assign: {allocationRemainder}%
                    </span>
                  )}
                {isBeneficiaryShareTotalOver100(allocationRunningTotal) && (
                  <span className="block mt-1 text-xs font-normal">
                    Total cannot exceed 100%. Lower one or more shares before
                    saving.
                  </span>
                )}
              </div>
              <div className="space-y-4">
                {allocationIndices.length === 0 && (
                  <p className="text-sm text-gray-600">
                    No {allocationType} beneficiaries are listed for this
                    account yet. Use{" "}
                    <strong>Add {allocationType} beneficiary</strong> below, or
                    add them from the Beneficiaries section.
                  </p>
                )}
                {allocationIndices.map((idx) => {
                  const b = benListForAllocation[idx];
                  const shareVal =
                    parseFloat(
                      getFormValue(`beneficiaries.${idx}.share`, b?.share),
                    ) || 0;
                  const isNewRow = !b?.id || b.__isNew;
                  return (
                    <div
                      key={b?.id || `new-${idx}`}
                      className="border border-gray-200 rounded-lg p-4 space-y-3"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        {isNewRow ? (
                          <div className="flex-1 min-w-[200px]">
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                              Household member
                            </label>
                            <select
                              value={
                                getFormValue(
                                  `beneficiaries.${idx}.household_member_id`,
                                  b?.household_member_id,
                                ) || ""
                              }
                              onChange={(e) =>
                                handleFieldChange(
                                  `beneficiaries.${idx}.household_member_id`,
                                  e.target.value || "",
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                            >
                              <option value="">Select member</option>
                              {household?.members?.map((m) => {
                                if (ownerMemberIds.has(m.id)) return null;
                                const takenByOther = benListForAllocation.some(
                                  (row, j) =>
                                    j !== idx &&
                                    row?.household_member_id === m.id,
                                );
                                if (
                                  takenByOther &&
                                  m.id !== b?.household_member_id
                                ) {
                                  return null;
                                }
                                const fn = m.personal_info?.first_name || "";
                                const ln = m.personal_info?.last_name || "";
                                const label = `${fn} ${ln}`.trim() || "Member";
                                return (
                                  <option key={m.id} value={m.id}>
                                    {label}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        ) : (
                          <div className="text-sm font-semibold text-gray-900">
                            {getMemberDisplayName(b?.household_member_id)}
                          </div>
                        )}
                        {isNewRow && (
                          <button
                            type="button"
                            onClick={() => removeBeneficiaryRow(idx)}
                            className="text-sm font-medium text-gray-600 hover:text-red-600"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Share %
                          </label>
                          <SharePresetButtons
                            value={shareVal}
                            onChange={(val) =>
                              handleFieldChange(
                                `beneficiaries.${idx}.share`,
                                val,
                              )
                            }
                          />
                        </div>
                        <ShareSlider
                          value={shareVal}
                          onChange={(val) =>
                            handleFieldChange(`beneficiaries.${idx}.share`, val)
                          }
                        />
                      </div>
                    </div>
                  );
                })}
                {allocationType && (
                  <button
                    type="button"
                    onClick={addBeneficiaryRow}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100"
                  >
                    + Add {allocationType} beneficiary
                  </button>
                )}
              </div>
            </div>
          )}

          {isNonEditable && (
            <div className="flex items-start gap-3 border border-amber-200 bg-amber-50 rounded-lg p-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                {NON_EDITABLE_PENDING_FIELD_MESSAGES[selectedField]}
              </p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {isNonEditable ? "Close" : "Cancel"}
          </button>
          {!isNonEditable && (
            <button
              onClick={handleSave}
              disabled={saving || (isBankSection ? routingInvalid : lplInvalid)}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <svg
                    className="animate-spin w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Save & Next
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default MissingAccountFieldModal;
